"use client";

import { useExternalLearningSettings } from "./learning-settings-context";
import { useBrowserPreference } from "../hooks/useBrowserPreference";
import React, { useEffect, useState, useRef, useCallback, useMemo, useSyncExternalStore, Suspense } from "react";
import { createPortal } from "react-dom";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { useArticleShellSlots } from "./article-shell/ArticleShellContext";
import { useId } from "react";
import { useUser, useAuth } from "@clerk/nextjs";
import { useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import { useProblemDataAdapter, type SaveProblemHistoryResult } from "./ProblemDataAdapter";
import { useProblemAnswerSlots } from "./ProblemAnswerSlots";
import { isConfirmDismissed, setConfirmDismissed } from "../lib/dismissible-confirm";
import type {
  ProblemHistory,
  ErrorNote,
  ProblemAttemptPartialScore,
  ProblemAttempt,
} from "../types/problem";
import BlockEditor from "./BlockEditor";
import RelatedTheory from "./RelatedTheory";
import type { RelatedTheoryLink } from "../lib/related-theory";
import {
  getAnonymousSessionId,
  incrementProblemSolvedCount,
  hasGA5ProblemsConversionSent,
  markGA5ProblemsConversionSent,
} from "../lib/anonymousSession";
import { sendGAEventSafe } from "../lib/ga";
import {
  sendMetaPixelCustomEvent,
  hasMetaPixel5ProblemsSent,
  markMetaPixel5ProblemsSent,
} from "../lib/metaPixel";
import {
  Check,
  BookOpen,
  X,
  Bookmark,
  Lightbulb,
  Eye,
  EyeOff,
  Lock,
  LockOpen,
  Crown,
  MessageSquare,
  Sparkles,
  ThumbsDown,
  Plus,
  History,
} from "lucide-react";
import { getForumUrl } from "../lib/subject-utils";
import FeatureLockedModal from "./FeatureLockedModal";
import MilestonePopup, { MilestoneType } from "../components/MilestonePopup";
import { checkNewMilestone, markMilestoneAsShown } from "../lib/milestoneUtils";
import { useExamScore } from "./ExamScoreContext";
import {
  scoreStatusFromResult,
  type ProblemScoreStatus,
} from "../lib/problem-score-status";
import PreviousAnswers from "./PreviousAnswers";
import ProblemContentRenderer from "./ProblemContentRenderer";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "./ui/tooltip";
import { HelpCircle } from "lucide-react";
import ErrorNoteImageUpload from "./ErrorNoteImageUpload";
import TrialActivatedModal from "./TrialActivatedModal";
import toast from "react-hot-toast";
import { useProblemHistory } from "./ProblemHistoryContext";
import { useResolveNoteSession } from "./ExamSessionContext";
import {
  isObjectAnswer,
  isAnswerSets,
  mergeHiddenCorrectAnswers,
  type HiddenCorrectAnswersType,
  findBestMatchingSet,
  getSortedKeys,
  getSortedEntries,
  checkAnswerCorrectness,
  getItemCorrectness,
  getAnswerScore,
  normalizeForComparison,
  type CorrectAnswerType,
} from "../lib/problemAnswerUtils";
import { PASS_FEATURES } from "../types/pass";
import { useEffectivePass } from "../hooks/useEffectivePass";
import { useSolutionRevealAd } from "../hooks/useSolutionRevealAd";
import { uploadBlockEditorImages } from "../lib/block-editor-image-upload";
import * as Sentry from "@sentry/nextjs";
import { handleServerActionError, retryServerAction } from "../lib/serverActionUtils";
import { getTokenWithRetry } from "../lib/utils";
import { useChoicesVisibility } from "./ChoicesVisibilityContext";
import { useErrorNoteReview } from "./ErrorNoteReviewContext";
import {
  clearProblemAnswerDraft,
  clearProblemAnswerSessionState,
  getProblemAnswerDraft,
  getProblemAnswerSessionState,
  saveProblemAnswerDraft,
  saveProblemAnswerSessionState,
  type ProblemAnswerValue,
  type ProblemAnswerDraftValue,
} from "../lib/random-problems-persistence";
import { isDraftCompatible } from "../lib/problem-answer-draft-compatibility";
import {
  getProblemAnswerScrollAnchorPosition,
  getInlineSolutionCollapseScrollBehavior,
  restoreProblemAnswerScrollPosition,
} from "../lib/problemAnswerScroll";
import {
  ProblemAnswerCard,
  ProblemAnswerNumber,
  ProblemAnswerTable,
} from "@workspace/ui/components/problem-answer";

// 현재 timestamp 반환 헬퍼 (컴포넌트 외부 - react-hooks/purity 회피)
const nowTimestamp = (): number => Date.now();
const ERROR_NOTE_AUTO_SAVE_DELAY_MS = 8000;
type ErrorNoteDraft = Pick<ErrorNote, "content" | "images">;
type SavedErrorNote = ErrorNoteDraft & Pick<ErrorNote, "updatedAt">;
type ErrorNoteSaveState = {
  pending: ErrorNoteDraft[];
  latestRequest: ErrorNoteDraft;
  failed: boolean;
};

function isSameErrorNoteImages(a: string[] = [], b: string[] = []) {
  return a.length === b.length && a.every((image, index) => image === b[index]);
}

function isSameErrorNoteDraft(a: ErrorNoteDraft, b: ErrorNoteDraft) {
  return a.content === b.content && isSameErrorNoteImages(a.images, b.images);
}

// 무채점 "바로 정답 보기" 확인 다이얼로그 "다시 보지 않기" 저장 키 (JCG-2510). prefix 관례: jcg:
const REVEAL_CONFIRM_DISMISSED_KEY = "jcg:reveal-confirm:dismissed";
type RevealConfirmMode = "empty" | "partial";

// useSyncExternalStore용 subscribe/snapshot 헬퍼 (set-state-in-effect 회피용, JCG-2029)
const subscribeWindowResize = (callback: () => void) => {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("resize", callback);
  return () => window.removeEventListener("resize", callback);
};
const getIsSmallScreenSnapshot = () =>
  typeof window !== "undefined" && window.innerWidth <= 1280;
const getIsMobileScreenSnapshot = () =>
  typeof window !== "undefined" && window.innerWidth <= 768;
const getServerFalseSnapshot = () => false;

const getIsTouchDeviceSnapshot = () =>
  typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;
const subscribeNoop = () => () => {};
const draftSessionKeys = new Set<string>();

const subscribeFinePointer = (callback: () => void) => {
  if (typeof window === "undefined") return () => {};
  const mql = window.matchMedia("(hover: hover) and (pointer: fine)");
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
};
// 터치 불가 기기는 항상 true, 터치 가능 기기는 fine pointer 보유 시 true (JCG-1654)
const getHasKeyboardSnapshot = () => {
  if (typeof window === "undefined") return true;
  const isTouch = navigator.maxTouchPoints > 0;
  if (!isTouch) return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
};
const getServerTrueSnapshot = () => true;
const answerInputAutofillProps = {
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "none",
  spellCheck: false,
} as const;

// hydration 감지: client에서는 항상 true, server에서는 false
const getIsMountedSnapshot = () => true;

// 상대적 시간 표시 함수
const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return "방금 전";
  if (minutes < 60) return `${minutes}분 전`;
  if (hours < 24) return `${hours}시간 전`;
  if (days < 7) return `${days}일 전`;

  // 7일 이상이면 날짜 표시
  const date = new Date(timestamp);
  const month = date.getMonth() + 1;
  const day = date.getDate();
  return `${month}월 ${day}일`;
};

// ProblemHistory에서 ErrorNote 배열 추출 (레거시 호환)
function getErrorNotesFromHistory(history: ProblemHistory | null | undefined): ErrorNote[] {
  if (!history) return [];
  if (history.error_notes && history.error_notes.length > 0) {
    return history.error_notes;
  }
  if (history.error_note) {
    return [{
      id: "legacy",
      content: history.error_note,
      images: history.error_note_images || [],
      // JCG-2059: 서버 getErrorNotesFromData와 폴백 통일(|| 0) — 회차 뱃지 계산 결정성 확보
      createdAt: history.error_note_updated_at || 0,
      updatedAt: history.error_note_updated_at || 0,
    }];
  }
  return [];
}

/**
 * examSession 문자열을 파싱하여 기출 시험 페이지 URL을 반환합니다.
 * past-exam 페이지가 존재하는 실기 2023년 이후만 링크를 생성합니다.
 * @example parseExamSessionUrl("[실기]24년1회") → "/past-exam/2024/2024-1"
 * @example parseExamSessionUrl("[실기]21년1회") → null (페이지 미존재)
 * @example parseExamSessionUrl("[필기]24년2회") → null (필기 페이지 미존재)
 */
function parseExamSessionUrl(session: string): string | null {
  const match = session.match(/^\[(.+?)\](\d{2})년(\d)회$/);
  if (!match) return null;

  const [, type, shortYear, round] = match;
  if (type !== "실기") return null;

  const year = Number(shortYear);
  if (year < 23) return null;

  const fullYear = `20${shortYear}`;
  return `/past-exam/${fullYear}/${fullYear}-${round}`;
}

export interface ProblemAnswerProps {
  id?: string;
  problem: string;
  correctAnswer: CorrectAnswerType;
  examSessions?: string[];
  choices?: string[];
  solution?: string;
  shuffle?: boolean;
  ignoreOrder?: boolean;
  choiceCopy?: boolean;
  alwaysShowChoices?: boolean;
  disableHistory?: boolean;
  /** 관리자 문제 미리보기에서만 비저장 상태로 우측 풀이 패널을 사용한다. */
  previewSolutionInShell?: boolean;
  defaultErrorNoteOpen?: boolean;
  strictWhitespace?: boolean;
  caseSensitive?: boolean;
  /** SQL 문자열 리터럴 밖의 `AS "단순별칭"`과 `AS 별칭`만 동등하게 채점한다. */
  allowQuotedSqlAlias?: boolean;
  notRecommended?: boolean;
  notRecommendedReason?: string;
  defaultSolutionOpen?: boolean;
  problemType?: "기초" | "비추천" | "기출변형" | "기출";
  problemTypes?: ("기초" | "비추천" | "기출변형" | "기출")[];
  variationNote?: string;
  premium?: boolean;
  gridCols?: 2 | 3 | 4;
  hiddenCorrectAnswers?: HiddenCorrectAnswersType;
  compareAsSet?: boolean;
  tags?: string[];
  answerSeparator?: string;
  /** JCG-2320: 풀이 상단 "관련 이론" 링크 바 데이터(경로→제목 해석 완료). 비어있으면 바 미표시. */
  relatedTheory?: RelatedTheoryLink[];
  problemNumber?: number;
  totalProblems?: number;
  // 노트 작성 시각(createdAt)을 회차 라벨로 변환 (활성 회차면 null → 뱃지 없음). JCG-2059
  resolveNoteSession?: (createdAtMs: number) => string | null;
  persistDraft?: boolean;
  persistDraftAcrossReload?: boolean;
}

function createEmptyUserAnswer(
  correctAnswer: CorrectAnswerType
): string | Record<string, string | number> {
  if (isAnswerSets(correctAnswer)) {
    const initialAnswers: Record<string, string | number> = {};
    getSortedKeys(correctAnswer[0] as Record<string, string | number | string[]>).forEach((key) => {
      initialAnswers[key] = "";
    });
    return initialAnswers;
  }

  if (isObjectAnswer(correctAnswer)) {
    const initialAnswers: Record<string, string | number> = {};
    getSortedKeys(correctAnswer).forEach((key) => {
      initialAnswers[key] = "";
    });
    return initialAnswers;
  }

  return "";
}

function mergeDraftWithEmptyAnswer(
  draft: ProblemAnswerDraftValue,
  correctAnswer: CorrectAnswerType
): ProblemAnswerValue {
  const emptyAnswer = createEmptyUserAnswer(correctAnswer);
  if (typeof emptyAnswer === "string") return draft;

  return {
    ...emptyAnswer,
    ...(draft as Record<string, string | number>),
  };
}

function hasDraftAnswerValue(answer: ProblemAnswerValue): boolean {
  if (typeof answer === "string") {
    return answer.trim() !== "";
  }

  return Object.values(answer).some((value) => value.toString().trim() !== "");
}

/**
 * 오답노트 복습 모드(JCG-2060)에서 정답·풀이·오답노트 영역을 덮는 무채색 픽셀 모자이크 오버레이.
 *
 * 부모 영역에 `relative` 를 두고 이 오버레이를 `absolute inset-0` 으로 올린다. `<button>` 이라
 * 클릭/Enter/Space 로 해당 영역만 드러나고(onReveal), inset-0 이라 가린 동안 아래 콘텐츠(예:
 * BlockEditor)의 포인터 입력을 가로막는다. 픽셀 모양은 CSS `repeating-conic-gradient`(8px 타일,
 * 무채색)만으로 만든다 — 캔버스/이미지 없음. 다크모드는 토큰 페어로 분리.
 */
function MosaicOverlay({
  onReveal,
  label,
}: {
  onReveal: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onReveal}
      aria-label={`${label}가 가려져 있습니다. 눌러서 보기`}
      // 무채색 픽셀 체커보드: 불투명 base(bg) 위에 currentColor 체커(conic-gradient)를 8px 타일로
      // 반복한다. 색상은 Tailwind 클래스(bg=base, text=currentColor 체커)로 다크모드 분리하고,
      // 그래디언트 기하만 inline style 로 둬 Tailwind 임의값 파싱 이슈를 피한다.
      style={{
        backgroundImage:
          "repeating-conic-gradient(currentColor 0% 25%, transparent 0% 50%)",
        backgroundSize: "8px 8px",
      }}
      className="group absolute inset-0 z-10 flex cursor-pointer items-center justify-center rounded-md
                 bg-gray-200 text-gray-400 dark:bg-gray-700 dark:text-gray-500
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
    >
      <span className="sr-only">{label} 가려짐</span>
      <span
        className="flex items-center gap-1 rounded bg-white/85 px-2 py-1 text-xs font-medium text-gray-700 opacity-0 transition-opacity
                   group-hover:opacity-100 group-focus-visible:opacity-100 dark:bg-gray-900/85 dark:text-gray-200"
        aria-hidden
      >
        <Eye className="h-3.5 w-3.5" /> 눌러서 보기
      </span>
    </button>
  );
}

const ProblemAnswerContent: React.FC<ProblemAnswerProps> = ({
  id,
  problem,
  correctAnswer,
  examSessions,
  choices,
  solution,
  shuffle = true,
  ignoreOrder = false,
  choiceCopy = false,
  alwaysShowChoices = false,
  disableHistory = false,
  previewSolutionInShell = false,
  defaultErrorNoteOpen = false,
  strictWhitespace = false,
  caseSensitive = false,
  allowQuotedSqlAlias = false,
  notRecommended = false,
  notRecommendedReason,
  defaultSolutionOpen = false,
  problemType: problemTypeProp,
  problemTypes: problemTypesProp,
  variationNote,
  premium = false,
  gridCols: gridColsProp,
  hiddenCorrectAnswers,
  compareAsSet = false,
  tags,
  answerSeparator,
  relatedTheory,
  problemNumber,
  totalProblems,
  resolveNoteSession,
  persistDraft = false,
  persistDraftAcrossReload = false,
}) => {
  const componentId = useId();
  const recommendationsPanelId = `${componentId}-recommendations`;
  const pathname = usePathname();

  // JCG-2059: 노트별 회차 뱃지 resolver — prop 우선, 없으면 Context(전역 ExamSessionProvider) fallback.
  // 덕분에 문제 풀이 페이지 등 prop을 안 넘기는 곳에서도 뱃지가 표시됨.
  const ctxResolveNoteSession = useResolveNoteSession();
  const effectiveResolveNoteSession = resolveNoteSession ?? ctxResolveNoteSession;

  // hiddenCorrectAnswers가 있으면 correctAnswer와 합쳐서 검증용 답안 생성
  const validationCorrectAnswer = useMemo(
    () => mergeHiddenCorrectAnswers(correctAnswer, hiddenCorrectAnswers),
    [correctAnswer, hiddenCorrectAnswers]
  );

  // 문제 유형 자동 추론 (복수 배지 지원)
  const problemTypes = useMemo(() => {
    if (problemTypesProp && problemTypesProp.length > 0) return problemTypesProp;
    if (problemTypeProp) return [problemTypeProp];
    const types: ("기초" | "비추천" | "기출변형" | "기출")[] = [];
    if (examSessions && examSessions.length > 0) types.push("기출");
    if (notRecommended) types.push("비추천");
    return types.length > 0 ? types : undefined;
  }, [problemTypeProp, problemTypesProp, examSessions, notRecommended]);

  const [userAnswer, setUserAnswer] = useState<ProblemAnswerValue>(() =>
    createEmptyUserAnswer(correctAnswer)
  );
  const userAnswerRef = useRef<ProblemAnswerValue>(userAnswer);
  const [isCorrectAnswerVisible, setIsCorrectAnswerVisible] = useState(defaultSolutionOpen);
  const [isRevealedWithoutGrading, setIsRevealedWithoutGrading] = useState(false);
  const [isPartialAnswerReveal, setIsPartialAnswerReveal] = useState(false);
  // 무채점 "바로 정답 보기" 확인 다이얼로그 (JCG-2510): 실수로 정답을 보게 되는 것을 방지.
  const [showRevealConfirm, setShowRevealConfirm] = useState(false);
  const [revealConfirmMode, setRevealConfirmMode] = useState<RevealConfirmMode>("empty");
  const externalSettings = useExternalLearningSettings();
  const [desktopChoiceInput] = useBrowserPreference("jcg:desktop-choice-input", false);
  const [dontAskAgain, setDontAskAgain] = useState(false);
  const [isSolutionVisible, setIsSolutionVisible] = useState(defaultSolutionOpen);
  // hydration 감지: useSyncExternalStore로 SSR/client mismatch 없이 mount 여부 판단 (JCG-2029)
  const isMounted = useSyncExternalStore(
    subscribeNoop,
    getIsMountedSnapshot,
    getServerFalseSnapshot
  );
  const [problemHistory, setProblemHistory] = useState<ProblemHistory | null>(
    null
  );
  // JCG-3176: 정답 공개 시 "이전 답안 이력"에서 이번 제출분을 제외하기 위한 최초 로드 스냅샷
  const [previousAttemptsSnapshot, setPreviousAttemptsSnapshot] = useState<{
    id: string;
    attempts: ProblemAttempt[];
  } | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const processedSolution = solution ?? "";
  const [isErrorNoteOpen, setIsErrorNoteOpen] = useState(defaultErrorNoteOpen);
  const [errorNotes, setErrorNotes] = useState<ErrorNote[]>([]);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [errorNoteSaveStates, setErrorNoteSaveStates] = useState<Record<string, ErrorNoteSaveState>>({});
  const pendingErrorNoteDraftsRef = useRef<Map<string, Set<ErrorNoteDraft>>>(new Map());
  const isSavingErrorNote = Object.values(errorNoteSaveStates).some(state => state.pending.length > 0);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedNoteByIdRef = useRef<Map<string, SavedErrorNote>>(new Map());
  const inlineErrorNoteImageFileMapRef = useRef<Map<string, File>>(new Map());
  const errorNoteEditVersionRef = useRef<Map<string, number>>(new Map());
  const errorNoteLastEditAtRef = useRef<Map<string, number>>(new Map());
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const creatingNoteRef = useRef(false);
  const [isDeletingNote, setIsDeletingNote] = useState(false);
  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [editingTabTitle, setEditingTabTitle] = useState("");
  const [draggedNoteId, setDraggedNoteId] = useState<string | null>(null);
  const noteMetadataPendingRef = useRef(false);
  const [dragOverNoteId, setDragOverNoteId] = useState<string | null>(null);
  const [problemAnswerRef, setProblemAnswerRef] =
    useState<HTMLDivElement | null>(null);
  const attachProblemAnswerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (!element) return;
      setProblemAnswerRef((currentElement) =>
        currentElement === element ? currentElement : element
      );
    },
    []
  );
  const errorNoteEditorRef = useRef<HTMLDivElement>(null);
  const answerTextareaRef = useRef<HTMLTextAreaElement>(null);
  const choiceAnswerInputsRef = useRef<Map<string, HTMLInputElement>>(new Map());
  const activeChoiceAnswerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const lastChoiceAnswerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Safari에서는 blur.relatedTarget이 null이므로 실제 클릭·포커스 대상을 기준으로 해제한다.
  useEffect(() => {
    const handleInteraction = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const input = target.closest<HTMLInputElement | HTMLTextAreaElement>("[data-choice-answer]");
      if (input && problemAnswerRef?.contains(input) && !input.disabled) {
        activeChoiceAnswerRef.current = input;
        lastChoiceAnswerRef.current = input;
      } else if (!(target.closest("[data-choice-input]") && problemAnswerRef?.contains(target))) {
        activeChoiceAnswerRef.current = null;
      }
    };
    document.addEventListener("pointerdown", handleInteraction, true);
    document.addEventListener("focusin", handleInteraction, true);
    return () => {
      document.removeEventListener("pointerdown", handleInteraction, true);
      document.removeEventListener("focusin", handleInteraction, true);
    };
  }, [problemAnswerRef]);

  useEffect(() => {
    activeChoiceAnswerRef.current = null;
    lastChoiceAnswerRef.current = null;
  }, [id]);

  const separatorInputRefs = useRef<Map<number, HTMLInputElement>>(new Map());
  // 화면 크기/입력 장치 감지: useSyncExternalStore로 setState in effect 회피 (JCG-2029)
  const isSmallScreen = useSyncExternalStore(
    subscribeWindowResize,
    getIsSmallScreenSnapshot,
    getServerFalseSnapshot
  );
  const isMobileScreen = useSyncExternalStore(
    subscribeWindowResize,
    getIsMobileScreenSnapshot,
    getServerFalseSnapshot
  );
  const isTouchDevice = useSyncExternalStore(
    subscribeNoop,
    getIsTouchDeviceSnapshot,
    getServerFalseSnapshot
  );
  // 물리 키보드 보유 여부 (터치 노트북 등 하이브리드 기기 판별용, JCG-1654)
  // 터치 불가 기기는 항상 true, 터치 가능 기기는 (hover: hover) + (pointer: fine)로 판단
  const hasKeyboard = useSyncExternalStore(
    subscribeFinePointer,
    getHasKeyboardSnapshot,
    getServerTrueSnapshot
  );
  const [isHoveringSaved, setIsHoveringSaved] = useState(false);
  const [isShowingUnsaveFeedback, setIsShowingUnsaveFeedback] = useState(false);
  const [isRecommendationsOpen, setIsRecommendationsOpen] = useState(false);
  const [isChoicesVisible, setIsChoicesVisible] = useState(true);
  const [localChoicesOverride, setLocalChoicesOverride] = useState(false);
  const [featureLockedModal, setFeatureLockedModal] = useState<
    "errorNote" | "problemSave" | "problemHistory" | null
  >(null);
  const [showTrialActivatedModal, setShowTrialActivatedModal] = useState(false);
  // 마일스톤 팝업 상태
  const [milestonePopup, setMilestonePopup] = useState<{
    isOpen: boolean;
    milestone: MilestoneType;
    solvedCount: number;
    totalProblems: number;
  } | null>(null);
  const { isSignedIn, user, isLoaded: isUserLoaded } = useUser();

  // 풀이 데이터 접근은 앱이 주입한 어댑터를 통한다(앱마다 저장소가 다르다).
  const {
    getProblemHistoryById,
    getProblemHistoryBySessionId,
    saveProblemHistory,
    saveProblemHistoryAnonymous,
    saveErrorNote,
    createErrorNoteItem,
    saveErrorNoteItem,
    deleteErrorNoteItem,
    renameErrorNoteItem,
    reorderErrorNotes,
    saveProblemAsSaved,
    saveProblemAsSavedAnonymous,
    unsaveProblemAsSaved,
    unsaveProblemAsSavedAnonymous,
    getTotalProblemCount,
  } = useProblemDataAdapter();

  // 광고·추천·회차 이력 패널은 앱이 주입한다(앱마다 상품과 백엔드가 다르다).
  const {
    renderHistoryPanel,
    renderSolutionAd,
    renderRecommendations,
  } = useProblemAnswerSlots();

  const { getToken } = useAuth();
  const searchParams = useSearchParams();
  const { isPremiumOrAbove, passType, hasAccess: hasEffectiveAccess, isAdFree } = useEffectivePass();
  const {
    requestReveal: requestSolutionRevealAd,
    isAdOpen: isSolutionAdOpen,
    closeAd: closeSolutionAd,
    // 광고 모달은 앱이 주입한 슬롯이 렌더한다. 슬롯이 없으면 보류하지 않도록 알려준다.
  } = useSolutionRevealAd({ hasAdSlot: Boolean(renderSolutionAd) });
  const choicesVisibilityCtx = useChoicesVisibility();
  const globalChoicesHidden = choicesVisibilityCtx?.globalChoicesHidden ?? false;
  const isSqlAnswer = id?.startsWith("sql_") ?? false;
  const ignoreTrailingSemicolon = isSqlAnswer;
  const shouldPersistDraft = persistDraft && !!id && !disableHistory && !defaultSolutionOpen;
  const draftPersistenceKey = shouldPersistDraft && id ? `${pathname}:${id}` : null;
  const checkedDraftKeyRef = useRef<string | null>(null);
  const checkedSessionStateKeyRef = useRef<string | null>(null);
  const skipNextDraftSaveRef = useRef(false);
  const answerFieldNamePrefix = useMemo(() => {
    if (id) return `problem-answer-${id}`;
    const fallbackId = componentId.replace(/[^a-zA-Z0-9_-]/g, "");
    return `problem-answer-${fallbackId || "anonymous"}`;
  }, [componentId, id]);

  const persistDraftAnswer = useCallback(
    (nextAnswer: ProblemAnswerValue) => {
      if (!shouldPersistDraft || !id || isCorrectAnswerVisible) return;

      if (hasDraftAnswerValue(nextAnswer)) {
        saveProblemAnswerDraft(pathname, id, nextAnswer);
        clearProblemAnswerSessionState(pathname, id);
        return;
      }

      clearProblemAnswerDraft(pathname, id);
      clearProblemAnswerSessionState(pathname, id);
    },
    [shouldPersistDraft, id, pathname, isCorrectAnswerVisible]
  );

  const updateUserAnswer = useCallback(
    (nextAnswer: ProblemAnswerValue) => {
      persistDraftAnswer(nextAnswer);
      setUserAnswer(nextAnswer);
    },
    [persistDraftAnswer]
  );

  const updateLatestUserAnswer = useCallback(
    (nextAnswer: ProblemAnswerValue) => {
      userAnswerRef.current = nextAnswer;
      updateUserAnswer(nextAnswer);
    },
    [updateUserAnswer]
  );

  const persistRevealedSessionState = useCallback(
    (
      answer: ProblemAnswerValue,
      isRevealedWithoutGradingValue: boolean,
      isPartialAnswerRevealValue = false,
      scoreStatus?: ProblemScoreStatus,
      scoreRatio?: number
    ) => {
      if (!shouldPersistDraft || !id) return;

      clearProblemAnswerDraft(pathname, id);
      saveProblemAnswerSessionState(pathname, id, {
        answer,
        isCorrectAnswerVisible: true,
        isRevealedWithoutGrading: isRevealedWithoutGradingValue,
        isPartialAnswerReveal: isPartialAnswerRevealValue,
        ...(scoreStatus ? { scoreStatus } : {}),
        ...(typeof scoreRatio === "number" ? { scoreRatio } : {}),
      });
    },
    [shouldPersistDraft, id, pathname]
  );

  useEffect(() => {
    userAnswerRef.current = userAnswer;
  }, [userAnswer]);

  useEffect(() => {
    if (!shouldPersistDraft || !id || !draftPersistenceKey || isCorrectAnswerVisible) {
      checkedDraftKeyRef.current = null;
      checkedSessionStateKeyRef.current = null;
      skipNextDraftSaveRef.current = false;
      return;
    }

    if (!draftSessionKeys.has(draftPersistenceKey)) {
      draftSessionKeys.add(draftPersistenceKey);
      if (!persistDraftAcrossReload) {
        clearProblemAnswerDraft(pathname, id);
        clearProblemAnswerSessionState(pathname, id);
      }
    }

    if (checkedSessionStateKeyRef.current !== draftPersistenceKey) {
      checkedSessionStateKeyRef.current = draftPersistenceKey;
      const sessionState = getProblemAnswerSessionState(pathname, id);
      if (sessionState && isDraftCompatible(sessionState.answer, correctAnswer)) {
        checkedDraftKeyRef.current = draftPersistenceKey;
        userAnswerRef.current = sessionState.answer;
        skipNextDraftSaveRef.current = true;
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUserAnswer(sessionState.answer);
        setIsRevealedWithoutGrading(sessionState.isRevealedWithoutGrading);
        setIsPartialAnswerReveal(sessionState.isPartialAnswerReveal ?? false);
        setIsCorrectAnswerVisible(sessionState.isCorrectAnswerVisible);
        return;
      }
    }

    if (checkedDraftKeyRef.current === draftPersistenceKey) return;
    checkedDraftKeyRef.current = draftPersistenceKey;
    if (hasDraftAnswerValue(userAnswer)) return;

    const draft = getProblemAnswerDraft(pathname, id);
    if (!draft || !isDraftCompatible(draft, correctAnswer)) return;

    skipNextDraftSaveRef.current = true;
    const draftAnswer = mergeDraftWithEmptyAnswer(draft, correctAnswer);
    userAnswerRef.current = draftAnswer;
    setUserAnswer(draftAnswer);
  }, [
    shouldPersistDraft,
    id,
    pathname,
    correctAnswer,
    isCorrectAnswerVisible,
    draftPersistenceKey,
    userAnswer,
    persistDraftAcrossReload,
  ]);

  useEffect(() => {
    if (!shouldPersistDraft || !id || !draftPersistenceKey) return;
    if (checkedDraftKeyRef.current !== draftPersistenceKey) return;

    if (skipNextDraftSaveRef.current) {
      skipNextDraftSaveRef.current = false;
      return;
    }

    if (isCorrectAnswerVisible || !hasDraftAnswerValue(userAnswer)) {
      clearProblemAnswerDraft(pathname, id);
      return;
    }

    saveProblemAnswerDraft(pathname, id, userAnswer);
  }, [shouldPersistDraft, id, pathname, userAnswer, isCorrectAnswerVisible, draftPersistenceKey]);

  useEffect(() => {
    if (typeof userAnswer !== "string") return;

    const textarea = answerTextareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [userAnswer]);

  // 오답노트 복습 가리기(JCG-2060): Provider 없는 페이지(이론/코딩/시험)는 null → 기존 동작 유지.
  // mosaicHidden 이면 정답(가운데)·풀이/오답노트(우측 셸 패널)를 무채색 픽셀 모자이크로 덮는다
  // (영역 클릭 시 드러남). 풀이/오답노트는 다른 셸 페이지와 동일하게 우측 사이드바로 열린다
  // (useShellSolution 우회하지 않음).
  const errorNoteReviewCtx = useErrorNoteReview();
  const mosaicHidden = errorNoteReviewCtx?.mosaicHidden ?? false;
  // 오답노트 복습 페이지에서 스크롤 중(화면 중앙)인 활성 문제면, 우측 풀이/오답노트를 자동으로 연다.
  const isActiveReviewPanel =
    !!errorNoteReviewCtx && !!id && errorNoteReviewCtx.activeProblemId === id;
  const reviewPanelAutoOpenDismissedRef = useRef(false);
  // auto-open 이펙트가 직전 실행 때의 defaultSolutionOpen 을 기억해, 풀기↔복습 토글로
  // 값이 바뀐 커밋을 감지한다. 그 커밋에선 셸 activeSolutionId 가 직전 exitSolution 여파로
  // null 로 남아 있을 수 있어, 이를 사용자의 풀이 닫기로 오인해 복습 auto-open 을 억제하지 않는다.
  const prevDefaultSolutionOpenForAutoOpenRef = useRef(defaultSolutionOpen);

  // 모자이크 영역별 로컬 reveal(클릭으로 해당 영역만 드러냄). 전역 가리기 토글(mosaicHidden)이
  // 바뀌면 reveal 을 리셋한다 — 렌더 시점 prev 패턴(set-state-in-effect 금지, JCG-2029).
  const [mosaicReveal, setMosaicReveal] = useState({
    answer: false,
    solution: false,
    note: false,
  });
  const [prevMosaicHidden, setPrevMosaicHidden] = useState(mosaicHidden);
  if (prevMosaicHidden !== mosaicHidden) {
    setPrevMosaicHidden(mosaicHidden);
    setMosaicReveal({ answer: false, solution: false, note: false });
  }
  // 각 영역의 실제 가림 여부 (가리기 ON 이고 아직 reveal 안 함)
  const answerMosaic = mosaicHidden && !mosaicReveal.answer;
  const solutionMosaic = mosaicHidden && !mosaicReveal.solution;
  const noteMosaic = mosaicHidden && !mosaicReveal.note;

  // starter 이상(광고 미노출) 데스크탑에서 풀이/오답노트를 사이드 패널로 배치
  const useSideLayout = !isSmallScreen && isAdFree;

  // 풀이 모드(JCG-2065): ArticleShell 셸 안(데스크탑 이론/코딩) + ad-free 이면 풀이/오답노트를
  // 인라인 사이드 패널 대신 우측 사이드바를 변신시켜 portal 한다. 셸은 ≥1024px 데스크탑에서만
  // 렌더되므로 inShell 이 데스크탑 컨텍스트를 함의 — isSmallScreen 검사는 불필요(중복).
  // 셸 밖(모바일/시험 페이지) 또는 free 사용자는 inShell=false 또는 아래 분기로 기존 동작 유지.
  const {
    inShell,
    solutionSlot,
    errorNoteSlot,
    activeSolutionId,
    wrongOpen,
    solutionCollapsed,
    enterSolution,
    exitSolution,
    setWrongOpen,
    setSplitRatio,
  } = useArticleShellSlots();
  // 관리자 문제 미리보기만 명시적으로 비저장 풀이 패널을 허용한다. 다른 disableHistory
  // 미리보기(복원 게시판 등)는 기존처럼 인라인으로 남고, 일반 학습 화면의 이용권 정책도 유지한다.
  const useShellSolution = inShell && (
    (isAdFree && !disableHistory) || (disableHistory && previewSolutionInShell)
  );

  // 현재 활성 오답노트
  const activeNote = useMemo(
    () => errorNotes.find(n => n.id === activeNoteId) ?? null,
    [errorNotes, activeNoteId]
  );

  const getErrorNoteEditVersion = useCallback((noteId: string) => {
    return errorNoteEditVersionRef.current.get(noteId) ?? 0;
  }, []);

  const bumpErrorNoteEditVersion = useCallback((noteId: string) => {
    errorNoteLastEditAtRef.current.set(noteId, Date.now());
    errorNoteEditVersionRef.current.set(
      noteId,
      (errorNoteEditVersionRef.current.get(noteId) ?? 0) + 1,
    );
  }, []);

  const hasUnsavedErrorNote = useCallback((note: ErrorNote) => {
    const savedNote = lastSavedNoteByIdRef.current.get(note.id);
    return !savedNote || !isSameErrorNoteDraft(note, savedNote);
  }, []);

  const handleInlineErrorNoteImageFilesChange = useCallback(
    (nextMap: Map<string, File>) => {
      // 같은 초안을 표시하는 다른 편집기는 blob URL만 받아 File 없는 Map을 보낼 수 있다.
      // 각 편집기가 가진 File을 모으고, 저장 성공·노트 전환에서만 명시적으로 비운다.
      for (const [url, file] of nextMap) {
        inlineErrorNoteImageFileMapRef.current.set(url, file);
      }
    },
    [],
  );

  // answerSeparator 문제의 키 목록 (보기 클릭→입력 기능에서도 사용)
  const separatorKeys = useMemo(() => {
    if (!answerSeparator || (!isAnswerSets(correctAnswer) && !isObjectAnswer(correctAnswer))) return [];
    return getSortedKeys(
      isAnswerSets(correctAnswer)
        ? (correctAnswer[0] as Record<string, string | number | string[]>)
        : (correctAnswer as Record<string, string | number | string[]>)
    );
  }, [answerSeparator, correctAnswer]);

  const separatorPrimaryValues = useMemo(() => {
    if (!answerSeparator || separatorKeys.length === 0) return [];
    const answerMap = isAnswerSets(correctAnswer)
      ? (correctAnswer[0] as Record<string, string | number | string[]>)
      : isObjectAnswer(correctAnswer)
        ? (correctAnswer as Record<string, string | number | string[]>)
        : null;

    if (!answerMap) return [];

    return separatorKeys
      .map((key) => {
        const value = answerMap[key];
        return Array.isArray(value) ? value[0] : value;
      })
      .filter((value): value is string | number => value !== undefined);
  }, [answerSeparator, separatorKeys, correctAnswer]);

  const shouldAutoAdvanceSeparatorInput = useMemo(() => {
    if (!answerSeparator || choices?.length || separatorPrimaryValues.length === 0) {
      return false;
    }

    return separatorPrimaryValues.every((value) => {
      const normalized = normalizeForComparison(
        value,
        strictWhitespace,
        caseSensitive,
        ignoreTrailingSemicolon,
        allowQuotedSqlAlias
      );
      return Array.from(normalized).length === 1;
    });
  }, [
    answerSeparator,
    choices,
    separatorPrimaryValues,
    strictWhitespace,
    caseSensitive,
    ignoreTrailingSemicolon,
    allowQuotedSqlAlias,
  ]);

  const separatorInputMode = useMemo<React.HTMLAttributes<HTMLInputElement>["inputMode"]>(() => {
    if (!shouldAutoAdvanceSeparatorInput) return "text";
    return separatorPrimaryValues.every((value) =>
      /^\d$/.test(
        normalizeForComparison(
          value,
          strictWhitespace,
          caseSensitive,
          ignoreTrailingSemicolon,
          allowQuotedSqlAlias
        )
      )
    )
      ? "numeric"
      : "text";
  }, [
    shouldAutoAdvanceSeparatorInput,
    separatorPrimaryValues,
    strictWhitespace,
    caseSensitive,
    ignoreTrailingSemicolon,
    allowQuotedSqlAlias,
  ]);

  // answerSeparator + choices 조합: 보기 클릭으로 답변란 채우기 활성화
  const isAnswerSeparatorWithChoices = !!answerSeparator && !!choices && choices.length > 0 && separatorKeys.length > 0;

  // 보기가 "완성된 정답 선택지"(객관식형)인지, 토큰 조합용 조각(코딩 빈칸 등)인지 판별 (JCG-2377)
  // 채점 정규화 기준으로 정답(변형 포함)이 보기 안에 존재하면 클릭→자동입력이 의미 있음.
  // 토큰 조합형(보기=연산자/문자 조각, 정답=조합)은 false → 자동입력 비활성(데스크톱과 동일하게 단순 표시).
  const choicesAreSelectableAnswers = useMemo(() => {
    if (!choices || choices.length === 0) return false;
    const normChoices = new Set(
      choices.map((c) =>
        normalizeForComparison(
          c,
          strictWhitespace,
          caseSensitive,
          ignoreTrailingSemicolon,
          allowQuotedSqlAlias
        )
      )
    );
    const variantInChoices = (val: string | number | string[]): boolean =>
      Array.isArray(val)
        ? val.some((v) =>
            normChoices.has(
              normalizeForComparison(
                v,
                strictWhitespace,
                caseSensitive,
                ignoreTrailingSemicolon,
                allowQuotedSqlAlias
              )
            )
          )
        : normChoices.has(
            normalizeForComparison(
              val,
              strictWhitespace,
              caseSensitive,
              ignoreTrailingSemicolon,
              allowQuotedSqlAlias
            )
          );

    const ca = validationCorrectAnswer;
    if (typeof ca === "string") {
      return variantInChoices(ca);
    }
    if (Array.isArray(ca)) {
      // answerSets(레코드 배열): 첫 세트의 모든 키 정답이 보기에 존재해야 함
      if (ca.length > 0 && typeof ca[0] === "object" && ca[0] !== null) {
        const rec = ca[0] as Record<string, string | number>;
        const keys = getSortedKeys(rec);
        return keys.length > 0 && keys.every((k) => variantInChoices(rec[k]));
      }
      // 단일 정답의 변형 배열(string[])
      return (ca as string[]).some((v) => variantInChoices(v));
    }
    if (ca && typeof ca === "object") {
      const rec = ca as Record<string, string | number | string[]>;
      const keys = getSortedKeys(rec);
      return keys.length > 0 && keys.every((k) => variantInChoices(rec[k]));
    }
    return false;
  }, [
    choices,
    validationCorrectAnswer,
    strictWhitespace,
    caseSensitive,
    ignoreTrailingSemicolon,
    allowQuotedSqlAlias,
  ]);

  // 터치 기기는 기존 자동입력을 유지하고, PC는 학습 설정을 켠 경우에만 허용한다 (JCG-3948).
  // 조립용 조각은 제외하며 separator·choiceCopy의 기존 입력/복사 방식은 보존한다.
  const isSelectableChoiceAutoFill =
    (isTouchDevice || desktopChoiceInput) && !isAnswerSeparatorWithChoices && !choiceCopy && choicesAreSelectableAnswers;
  // choiceCopy 문제 중 보기가 완성된 정답인 경우는 데스크톱·터치 공통으로 복사 대신 바로 입력한다.
  // 토큰 조합형은 커서 위치에 삽입하며 PC 설정과 무관하게 사용할 수 있다.
  const isChoiceCopyAutoFill =
    !isAnswerSeparatorWithChoices && choiceCopy && choicesAreSelectableAnswers;
  const isChoiceTokenInsert = !isAnswerSeparatorWithChoices && choiceCopy && !choicesAreSelectableAnswers;
  const isChoiceAutoFill = isSelectableChoiceAutoFill || isChoiceCopyAutoFill;

  // 전체 보기 숨기기 토글 시 로컬 오버라이드 초기화
  // 렌더 시점 prev-prop 패턴 사용 (set-state-in-effect 회피, JCG-2029)
  const [prevGlobalChoicesHidden, setPrevGlobalChoicesHidden] = useState(globalChoicesHidden);
  if (prevGlobalChoicesHidden !== globalChoicesHidden) {
    setPrevGlobalChoicesHidden(globalChoicesHidden);
    setLocalChoicesOverride(false);
  }

  // 답변 그리드 레이아웃: 2개→2열, 3개+→3열, 모바일은 1열
  const objectKeyCount = isAnswerSets(correctAnswer)
    ? Object.keys(correctAnswer[0]).length
    : isObjectAnswer(correctAnswer)
      ? getSortedKeys(correctAnswer).length
      : 0;
  const useGridLayout = objectKeyCount >= 2 && !isMobileScreen;
  const gridColsMap = { 2: "grid-cols-2", 3: "grid-cols-3", 4: "grid-cols-4" } as const;
  const gridColsClass = gridColsProp
    ? gridColsMap[gridColsProp]
    : objectKeyCount === 2
      ? "grid-cols-2"
      : "grid-cols-3";

  // 풀이이력 기능에 대한 프리미엄 접근 권한 확인
  const hasPremiumAccess = useMemo(() => {
    if (!isSignedIn || !user) return false;
    return hasEffectiveAccess(PASS_FEATURES.problemHistory);
  }, [isSignedIn, user, hasEffectiveAccess]);
  const hasErrorNoteAccess = Boolean(isSignedIn && user && hasEffectiveAccess(PASS_FEATURES.errorNotes));
  const hasLoadedHistoryRef = useRef(false);
  const historyLoadIdentityRef = useRef<{
    id: string | undefined;
    userId: string | undefined;
    hasErrorNoteAccess: boolean;
  } | null>(null);

  // ExamScoreContext는 optional (RandomizedProblems 내에서만 사용 가능)
  const examScore = useExamScore();

  // ProblemHistoryContext에서 배치 조회된 이력 가져오기
  const {
    histories: problemHistories,
    isLoading: isLoadingHistoriesFromContext,
  } = useProblemHistory();

  // 화면 크기/입력 장치 감지는 useSyncExternalStore로 이관됨 (상단 상태 선언부 참고, JCG-2029)

  // Activity로 숨겼던 라우트를 다시 보일 때도 effect가 재실행된다. 이때 기존 이력을
  // 다시 적용하면 제출 후의 로컬 이력이 오래된 배치 조회값으로 덮이므로,
  // 실제 문제/사용자/노트 접근 권한이 바뀐 경우에만 로드 상태를 초기화한다.
  useEffect(() => {
    const previous = historyLoadIdentityRef.current;
    if (
      !previous ||
      previous.id !== id ||
      previous.userId !== user?.id ||
      previous.hasErrorNoteAccess !== hasErrorNoteAccess
    ) {
      historyLoadIdentityRef.current = { id, userId: user?.id, hasErrorNoteAccess };
      hasLoadedHistoryRef.current = false;
    }
  }, [id, user?.id, hasErrorNoteAccess]);

  // 개발 모드 마일스톤 테스트용 (예: ?_testMilestone=50)
  const hasShownTestMilestoneRef = useRef(false);
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    if (hasShownTestMilestoneRef.current) return;

    const testMilestone = searchParams.get("_testMilestone");
    if (!testMilestone) return;

    const validMilestones = ["50", "100", "200", "all"];
    if (!validMilestones.includes(testMilestone)) return;

    const milestone = testMilestone === "all" ? "all" : (parseInt(testMilestone) as 50 | 100 | 200);

    hasShownTestMilestoneRef.current = true;

    // 약간의 딜레이 후 팝업 표시 (마운트 완료 후)
    const timer = setTimeout(() => {
      setMilestonePopup({
        isOpen: true,
        milestone,
        solvedCount: testMilestone === "all" ? 500 : parseInt(testMilestone),
        totalProblems: 500,
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchParams]);

  useEffect(() => {
    const loadHistory = async () => {
      if (!id || disableHistory || hasLoadedHistoryRef.current) return;

      // Clerk 사용자 정보 로딩이 완료될 때까지 기다림
      if (!isUserLoaded) {
        setIsLoadingHistory(true);
        return;
      }

      // Context가 아직 로딩 중이면 기다림 (배치 조회 완료 대기)
      if (isLoadingHistoriesFromContext) {
        setIsLoadingHistory(true);
        return;
      }

      // Context에서 배치 조회된 이력이 있으면 사용
      const contextHistory = problemHistories[id];
      if (contextHistory !== undefined) {
        setProblemHistory(contextHistory ?? null);
        // JCG-3176: 이 id의 "이전 답안" 스냅샷을 실제 로드된 값으로 확정 (레이스 회피).
        // mergedAttempts = canonical+전 회차 병합(회차 무관 전체 이력). 서버가 이미 읽은 값이라 추가 조회 없음.
        setPreviousAttemptsSnapshot({ id, attempts: contextHistory?.mergedAttempts ?? [] });
        setIsLoadingHistory(false);
        hasLoadedHistoryRef.current = true;

        // 오답노트 초기값 설정
        {
          const notes = hasErrorNoteAccess ? getErrorNotesFromHistory(contextHistory) : [];
          setErrorNotes(notes);
          lastSavedNoteByIdRef.current = new Map(
            notes.map((note) => [note.id, note]),
          );
          errorNoteEditVersionRef.current = new Map();
          errorNoteLastEditAtRef.current = new Map();
          setErrorNoteSaveStates({});
          if (notes.length > 0) {
            setActiveNoteId(notes[0].id);
          } else {
            setActiveNoteId(null);
          }
        }
        return;
      }

      // Context 로딩 완료 후에도 이력이 없으면 개별 조회 (fallback)
      hasLoadedHistoryRef.current = true;
      setIsLoadingHistory(true);
      try {
        if (isSignedIn && user) {
          // 로그인 사용자: 기존 로직
          const result = await getProblemHistoryById(id);
          if (result.success) {
            setProblemHistory(result.history);
            // JCG-3176: 이 id의 "이전 답안" 스냅샷을 실제 로드된 값으로 확정 (레이스 회피).
            // mergedAttempts = 회차 무관 전체 이력(서버가 이미 읽은 값, 추가 조회 없음).
            setPreviousAttemptsSnapshot({ id, attempts: result.history?.mergedAttempts ?? [] });
            // 오답노트 초기값 설정
            const notes = hasErrorNoteAccess ? getErrorNotesFromHistory(result.history) : [];
            setErrorNotes(notes);
            lastSavedNoteByIdRef.current = new Map(
              notes.map((note) => [note.id, note]),
            );
            errorNoteEditVersionRef.current = new Map();
            errorNoteLastEditAtRef.current = new Map();
            setErrorNoteSaveStates({});
            if (notes.length > 0) {
              setActiveNoteId(notes[0].id);
            } else {
              setActiveNoteId(null);
            }
          }
        } else {
          // 비로그인 사용자: 익명 세션으로 조회
          const sessionId = getAnonymousSessionId();
          if (sessionId) {
            const result = await getProblemHistoryBySessionId(sessionId, id);
            if (result.success) {
              setProblemHistory(result.history);
              // JCG-3176: 이 id의 "이전 답안" 스냅샷을 실제 로드된 값으로 확정 (레이스 회피).
              // mergedAttempts = 회차 무관 전체 이력(서버가 이미 읽은 값, 추가 조회 없음). 로그인/컨텍스트 경로와 동일 소스.
              setPreviousAttemptsSnapshot({ id, attempts: result.history?.mergedAttempts ?? [] });
              // 오답노트 초기값 설정
              const notes = hasErrorNoteAccess ? getErrorNotesFromHistory(result.history) : [];
              setErrorNotes(notes);
              lastSavedNoteByIdRef.current = new Map(
                notes.map((note) => [note.id, note]),
              );
              errorNoteEditVersionRef.current = new Map();
              errorNoteLastEditAtRef.current = new Map();
              setErrorNoteSaveStates({});
              if (notes.length > 0) {
                setActiveNoteId(notes[0].id);
              } else {
                setActiveNoteId(null);
              }
            }
          }
        }
      } catch (error) {
        // Server Action not found 에러인 경우 페이지 새로고침 안내
        if (handleServerActionError(error)) {
          return;
        }
        console.error("풀이 이력 로드 실패:", error);
        Sentry.captureException(error, {
          tags: {
            component: 'ProblemAnswer',
            operation: 'loadHistory'
          },
          extra: { problemId: id }
        });
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadHistory();
  }, [
    id,
    disableHistory,
    problemHistories,
    isLoadingHistoriesFromContext,
    isSignedIn,
    user,
    isUserLoaded,
    hasErrorNoteAccess,
  ]);

  // JCG-3176: 정답 공개 시 "이전 답안 이력"에서 이번 제출분을 제외하기 위해 최초 로드된 이력을
  // 스냅샷으로 고정한다. 스냅샷 세팅은 loadHistory가 실제 값을 받는 지점에서 수행한다 —
  // hasLoadedHistoryRef가 problemHistory state보다 먼저 true가 되는 레이스(빈 배열 조기 캡처)를 피하기 위함.
  // 이후 제출로 problemHistory.attempts가 갱신돼도(정답 공개 시 방금 답 append) 스냅샷은 그대로라
  // 이번 제출분이 "이전 답안"에서 자연히 제외된다. id 변경 시엔 파생값이 [] 로 떨어지고 재로드가 새 스냅샷을 채운다.
  const previousAttempts =
    previousAttemptsSnapshot && previousAttemptsSnapshot.id === id
      ? previousAttemptsSnapshot.attempts
      : [];

  // defaultErrorNoteOpen prop 변경 시 state 동기화
  // 렌더 시점 prev-prop 패턴 (set-state-in-effect 회피, JCG-2029)
  const [prevDefaultErrorNoteOpen, setPrevDefaultErrorNoteOpen] = useState(defaultErrorNoteOpen);
  if (prevDefaultErrorNoteOpen !== defaultErrorNoteOpen) {
    setPrevDefaultErrorNoteOpen(defaultErrorNoteOpen);
    setIsErrorNoteOpen(defaultErrorNoteOpen);
  }

  // defaultSolutionOpen prop 변경 시 state 동기화 — 오답노트 풀기 모드 토글이 런타임에 이 prop 을
  // 바꾼다(복습 ↔ 풀기 전환 시 정답/풀이 공개 상태를 함께 리셋). 다른 페이지는 정적 값이라 불변.
  // 렌더 시점 prev-prop 패턴 (set-state-in-effect 회피, JCG-2029)
  const [prevDefaultSolutionOpen, setPrevDefaultSolutionOpen] = useState(defaultSolutionOpen);
  if (prevDefaultSolutionOpen !== defaultSolutionOpen) {
    setPrevDefaultSolutionOpen(defaultSolutionOpen);
    setIsCorrectAnswerVisible(defaultSolutionOpen);
    setIsRevealedWithoutGrading(false);
    setIsPartialAnswerReveal(false);
    setIsSolutionVisible(defaultSolutionOpen);
    // 풀기↔복습 전환으로 정답 공개 상태가 리셋되면 무채점 확인 다이얼로그도 닫는다(JCG-2510).
    setShowRevealConfirm(false);
    setRevealConfirmMode("empty");
    setDontAskAgain(false);
  }

  const [prevUngradedRevealProblemId, setPrevUngradedRevealProblemId] = useState(id);
  if (prevUngradedRevealProblemId !== id) {
    setPrevUngradedRevealProblemId(id);
    setIsRevealedWithoutGrading(false);
    setIsPartialAnswerReveal(false);
    // 문제 전환 시 이전 문제의 무채점 확인 다이얼로그가 남지 않도록 닫는다(JCG-2510).
    setShowRevealConfirm(false);
    setRevealConfirmMode("empty");
    setDontAskAgain(false);
  }

  useEffect(() => {
    reviewPanelAutoOpenDismissedRef.current = false;
  }, [defaultSolutionOpen, id]);

  // 문제 전환/정답 숨김 시 이전 추천 패널 열린 상태가 다음 문제로 이어지지 않도록 닫는다.
  const [prevRecommendationsSyncDeps, setPrevRecommendationsSyncDeps] = useState({
    id,
    isCorrectAnswerVisible,
  });
  const recommendationsSyncDepsChanged =
    prevRecommendationsSyncDeps.id !== id ||
    prevRecommendationsSyncDeps.isCorrectAnswerVisible !== isCorrectAnswerVisible;
  if (recommendationsSyncDepsChanged) {
    setPrevRecommendationsSyncDeps({ id, isCorrectAnswerVisible });
    if (prevRecommendationsSyncDeps.id !== id || !isCorrectAnswerVisible) {
      setIsRecommendationsOpen(false);
    }
  }

  // 직전 2번 연속 정답 확인 (프리미엄 이상만)
  const isTwoConsecutiveCorrect = useMemo(() => {
    // 프리미엄 이상이 아니면 항상 false
    if (!isPremiumOrAbove) {
      return false;
    }
    if (!problemHistory || problemHistory.attempts.length < 2) {
      return false;
    }
    const attempts = problemHistory.attempts;
    const lastAttempt = attempts[attempts.length - 1];
    const secondLastAttempt = attempts[attempts.length - 2];
    return lastAttempt.isCorrect && secondLastAttempt.isCorrect;
  }, [problemHistory, isPremiumOrAbove]);

  // 전체 보기 숨기기 적용 시 유효 보기 상태
  // choiceCopy/alwaysShowChoices/answerSeparator 문제는 전체 숨기기 대상에서 제외
  // 2연속 정답 혜택은 전체 숨기기 모드에서는 숨기기 적용 (사용자 명시적 요청 우선)
  const effectiveChoicesVisible =
    choiceCopy || alwaysShowChoices || isAnswerSeparatorWithChoices
      ? isChoicesVisible
      : globalChoicesHidden
        ? localChoicesOverride
        : isChoicesVisible;
  const shouldShowChoiceHint =
    isAnswerSeparatorWithChoices ||
    isChoiceTokenInsert ||
    ((isChoiceAutoFill || isTwoConsecutiveCorrect) &&
      !isCorrectAnswerVisible &&
      effectiveChoicesVisible);
  const choiceHintText = isAnswerSeparatorWithChoices
    ? "보기를 클릭하여 순서대로 배치하세요 (다시 클릭하면 취소)"
    : isChoiceTokenInsert
      ? "보기를 클릭하면 답란의 커서 위치에 입력됩니다"
      : isChoiceAutoFill
        ? "보기를 클릭하면 답란에 자동 입력됩니다 (다시 클릭하면 취소)"
        : "클릭하면 복사가능";
  const showTwoConsecutiveChoiceHint =
    isTwoConsecutiveCorrect &&
    !choiceCopy &&
    !isCorrectAnswerVisible &&
    !isAnswerSeparatorWithChoices &&
    !isChoiceAutoFill;

  // 마지막 시도 확인 및 보기 비활성화 초기화 (프리미엄 이상만)
  // 렌더 시점 deps-change 패턴 (set-state-in-effect 회피, JCG-2029)
  // 사용자 클릭으로 isChoicesVisible를 직접 토글할 수 있어 useMemo로 완전 derive 불가
  // → deps 변경 시점에만 룰 기반 값으로 sync, 그 외에는 사용자 상태 보존
  const [prevChoicesSyncDeps, setPrevChoicesSyncDeps] = useState({
    problemHistory,
    id,
    isCorrectAnswerVisible,
    isTwoConsecutiveCorrect,
    isPremiumOrAbove,
    choiceCopy,
    alwaysShowChoices,
  });
  const choicesSyncDepsChanged =
    prevChoicesSyncDeps.problemHistory !== problemHistory ||
    prevChoicesSyncDeps.id !== id ||
    prevChoicesSyncDeps.isCorrectAnswerVisible !== isCorrectAnswerVisible ||
    prevChoicesSyncDeps.isTwoConsecutiveCorrect !== isTwoConsecutiveCorrect ||
    prevChoicesSyncDeps.isPremiumOrAbove !== isPremiumOrAbove ||
    prevChoicesSyncDeps.choiceCopy !== choiceCopy ||
    prevChoicesSyncDeps.alwaysShowChoices !== alwaysShowChoices;
  if (choicesSyncDepsChanged) {
    setPrevChoicesSyncDeps({
      problemHistory,
      id,
      isCorrectAnswerVisible,
      isTwoConsecutiveCorrect,
      isPremiumOrAbove,
      choiceCopy,
      alwaysShowChoices,
    });
    // 프리미엄 이상이 아니면 보기 비활성화를 적용하지 않음
    if (!isPremiumOrAbove) {
      setIsChoicesVisible(true);
    } else if (choiceCopy) {
      // choiceCopy 문제는 보기를 복사하거나 바로 입력하는 데 필요하므로 숨기지 않음
      setIsChoicesVisible(true);
    } else if (alwaysShowChoices) {
      // alwaysShowChoices 문제는 보기가 문제의 필수 요소이므로 숨기지 않음
      setIsChoicesVisible(true);
    } else if (isCorrectAnswerVisible) {
      // 현재 세션에서 이미 정답을 확인한 경우에는 보기 비활성화를 적용하지 않음
      // (no-op: 현재 상태 보존)
    } else if (problemHistory && problemHistory.attempts.length > 0) {
      const lastAttempt =
        problemHistory.attempts[problemHistory.attempts.length - 1];
      // 마지막 시도가 정답이고, 직전 2번 연속 정답이 아닌 경우에만 보기 비활성화
      if (lastAttempt.isCorrect && !isTwoConsecutiveCorrect) {
        setIsChoicesVisible(false);
      } else {
        // 마지막 시도가 오답이거나, 직전 2번 연속 정답인 경우 보기 활성화
        setIsChoicesVisible(true);
      }
    } else {
      // 이력이 없으면 보기 활성화
      setIsChoicesVisible(true);
    }
  }

  // 보기 셔플: SSR과 client mount 사이 hydration mismatch 방지를 위해 mount 전엔 원본 순서 유지
  // useMemo로 derive (set-state-in-effect 회피, JCG-2029). Math.random은 deps 변경 시 1회만 호출
  const shuffledChoices = useMemo<string[]>(() => {
    if (!choices) return [];
    if (!isMounted || !shuffle) return choices;
    // eslint-disable-next-line react-hooks/purity -- Math.random은 셔플 의도된 비결정성, deps 변경 시에만 재실행
    return [...choices].sort(() => Math.random() - 0.5);
  }, [choices, isMounted, shuffle]);

  // 마일스톤 체크 함수 — 저장 성공 결과의 풀이 수로 판정한다 (JCG-3864).
  // 풀이 수를 따로 조회하면 동시에 제출한 다른 문제의 저장이 섞여 달성 순간을 놓칠 수 있다.
  const checkMilestone = useCallback(async (saveResult: SaveProblemHistoryResult) => {
    // 이번 제출이 첫 풀이가 아니면 풀이 수가 오르지 않았으므로 마일스톤 체크 불필요
    const { isFirstAttempt, attemptedCount: solvedCount } = saveResult;
    if (!isFirstAttempt || solvedCount === undefined) return;

    try {
      const totalResult = await getTotalProblemCount();
      if (!totalResult.success) return;

      const totalProblems = totalResult.count;

      // 새로 달성한 마일스톤 확인
      const newMilestone = checkNewMilestone(solvedCount, totalProblems);

      if (newMilestone) {
        setMilestonePopup({
          isOpen: true,
          milestone: newMilestone,
          solvedCount,
          totalProblems,
        });
        markMilestoneAsShown(newMilestone);
      }
    } catch (error) {
      console.error("마일스톤 체크 실패:", error);
    }
  }, [getTotalProblemCount]);

  // answerSeparator 입력: 붙여넣기 시 separator로 분리하여 각 입력에 배분
  const handleSeparatorPaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, keys: string[]) => {
      const pastedText = e.clipboardData.getData("text").trim();
      if (!pastedText) return;

      let values: string[] = [];
      if (answerSeparator && pastedText.includes(answerSeparator)) {
        values = pastedText.split(answerSeparator).map((v) => v.trim());
      } else if (choices && choices.length > 0) {
        // choices가 있으면 공백/쉼표 분리 안 함 (보기에 공백이 포함될 수 있음)
        return;
      } else if (pastedText.includes(" ")) {
        values = pastedText.split(/\s+/).map((v) => v.trim());
      } else if (pastedText.includes(",")) {
        values = pastedText.split(",").map((v) => v.trim());
      } else if (
        keys.length > 1 &&
        pastedText.length === keys.length &&
        /^\d+$/.test(pastedText)
      ) {
        values = pastedText.split("");
      }

      if (values.length > 1) {
        e.preventDefault();
        const newAnswer: Record<string, string | number> = {};
        keys.forEach((key, index) => {
          newAnswer[key] = index < values.length ? values[index] : "";
        });
        updateUserAnswer(newAnswer);

        const focusIndex = Math.min(values.length, keys.length) - 1;
        setTimeout(() => {
          separatorInputRefs.current.get(focusIndex)?.focus();
        }, 0);
      }
    },
    [answerSeparator, choices, updateUserAnswer]
  );

  // answerSeparator + choices: 보기 클릭 시 다음 빈 입력란에 채우기
  const handleSeparatorChoiceClick = useCallback((choice: string) => {
    if (isCorrectAnswerVisible) return;
    const answers = userAnswerRef.current as Record<string, string | number>;

    // 이미 배치된 보기 클릭 → 해당 입력란 비우기
    const existingKey = separatorKeys.find(key => answers[key]?.toString() === choice);
    if (existingKey) {
      updateLatestUserAnswer({
        ...answers,
        [existingKey]: "",
      });
      return;
    }

    // 다음 빈 입력란 찾아서 채우기
    const emptyKey = separatorKeys.find(key => !answers[key] || answers[key].toString().trim() === "");
    if (emptyKey) {
      updateLatestUserAnswer({
        ...answers,
        [emptyKey]: choice,
      });
    }
  }, [isCorrectAnswerVisible, separatorKeys, updateLatestUserAnswer]);

  // 선택형 보기 클릭 시 답란 자동 입력 (JCG-2377, JCG-2380)
  const handleChoiceAutoFill = useCallback((choice: string) => {
    if (isCorrectAnswerVisible) return;

    // 단일 문자열 답변(textarea): 클릭 시 교체, 같은 보기 재클릭 시 비우기(토글)
    if (!isAnswerSets(correctAnswer) && !isObjectAnswer(correctAnswer)) {
      updateLatestUserAnswer(userAnswerRef.current === choice ? "" : choice);
      return;
    }

    // 객체형(여러 빈칸) 답변: 다음 빈 칸 채우기, 같은 보기 재클릭 시 해당 칸 비우기
    const keys = getSortedKeys(
      isAnswerSets(correctAnswer)
        ? (correctAnswer[0] as Record<string, string | number | string[]>)
        : (correctAnswer as Record<string, string | number | string[]>)
    );
    const answers = userAnswerRef.current as Record<string, string | number>;

    const activeInput = activeChoiceAnswerRef.current;
    const activeKey = activeInput?.dataset.choiceAnswer;
    const existingKey = keys.find(key => answers[key]?.toString() === choice);
    const targetKey = activeKey && keys.includes(activeKey)
      ? activeKey
      : existingKey ?? keys.find(key => !answers[key]?.toString().trim());
    if (!targetKey) return;

    const nextAnswers = { ...answers };
    if (existingKey) nextAnswers[existingKey] = "";
    const toggledOff = existingKey === targetKey;
    nextAnswers[targetKey] = toggledOff ? "" : choice;
    updateLatestUserAnswer(nextAnswers);
    if (toggledOff) return;

    // 현재 칸 다음부터 순환하여 빈칸을 찾는다 (앞쪽에서 옮겨온 보기도 다시 채울 수 있다).
    const targetIndex = keys.indexOf(targetKey);
    const remainingKeys = [...keys.slice(targetIndex + 1), ...keys.slice(0, targetIndex)];
    const nextKey = remainingKeys.find(key => !nextAnswers[key]?.toString().trim());
    const nextInput = nextKey ? choiceAnswerInputsRef.current.get(nextKey) : null;
    if (nextInput) {
      nextInput.focus({ preventScroll: true });
      activeChoiceAnswerRef.current = nextInput;
    } else {
      activeChoiceAnswerRef.current = null;
      if (activeInput === document.activeElement) activeInput?.blur();
    }
  }, [isCorrectAnswerVisible, correctAnswer, updateLatestUserAnswer]);

  const handleChoiceTokenInsert = useCallback((choice: string) => {
    if (isCorrectAnswerVisible) return;
    const isObject = isAnswerSets(correctAnswer) || isObjectAnswer(correctAnswer);
    const input = activeChoiceAnswerRef.current ?? lastChoiceAnswerRef.current ??
      (isObject ? choiceAnswerInputsRef.current.values().next().value : answerTextareaRef.current);
    if (!input || !input.isConnected || input.disabled) return;
    const key = input.dataset.choiceAnswer;
    const answers = userAnswerRef.current;
    const current = isObject ? (answers as Record<string, string | number>)[key!] : answers;
    const value = current?.toString() ?? "";
    const hasSelection = input === activeChoiceAnswerRef.current || input === lastChoiceAnswerRef.current;
    const start = hasSelection ? input.selectionStart ?? value.length : value.length;
    const end = hasSelection ? input.selectionEnd ?? start : value.length;
    const nextValue = value.slice(0, start) + choice + value.slice(end);
    updateLatestUserAnswer(isObject ? { ...(answers as Record<string, string | number>), [key!]: nextValue } : nextValue);
    // React가 controlled value를 반영하기 전에도 연속 클릭의 커서 위치를 최신으로 유지한다.
    input.value = nextValue;
    input.focus({ preventScroll: true });
    input.setSelectionRange(start + choice.length, start + choice.length);
    if (input instanceof HTMLTextAreaElement) {
      input.style.height = "auto";
      input.style.height = input.scrollHeight + "px";
    }
  }, [isCorrectAnswerVisible, correctAnswer, updateLatestUserAnswer]);

  const answerScore = useMemo(
    () =>
      getAnswerScore(
        userAnswer,
        validationCorrectAnswer,
        ignoreOrder,
        strictWhitespace,
        caseSensitive,
        compareAsSet,
        ignoreTrailingSemicolon,
        allowQuotedSqlAlias
      ),
    [
      userAnswer,
      validationCorrectAnswer,
      ignoreOrder,
      strictWhitespace,
      caseSensitive,
      compareAsSet,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias,
    ]
  );
  const partialScore: ProblemAttemptPartialScore | null = useMemo(
    () =>
      answerScore.totalItems > 1 &&
      answerScore.hasAnyAnswer &&
      answerScore.correctItems < answerScore.totalItems
        ? {
            correctItems: answerScore.correctItems,
            totalItems: answerScore.totalItems,
          }
        : null,
    [
      answerScore.correctItems,
      answerScore.hasAnyAnswer,
      answerScore.totalItems,
    ]
  );
  const partialScoreRatio =
    partialScore && partialScore.totalItems > 0
      ? partialScore.correctItems / partialScore.totalItems
      : 0;
  const shouldShowAnswerScore =
    isCorrectAnswerVisible &&
    !isRevealedWithoutGrading &&
    answerScore.totalItems > 1;
  const answerScorePercent =
    answerScore.totalItems > 0
      ? Math.round((answerScore.correctItems / answerScore.totalItems) * 100)
      : 0;
  const answerScoreClassName =
    answerScore.correctItems === answerScore.totalItems
      ? "border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300"
      : answerScore.correctItems > 0
        ? "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300"
        : "border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300";

  // 무채점 정답 공개 실제 로직 (JCG-2510에서 가드와 분리). 가드를 통과했을 때만 호출된다.
  const performRevealWithoutGrading = useCallback(() => {
    setIsRevealedWithoutGrading(true);
    setIsPartialAnswerReveal(false);
    toast("채점 없이 정답을 확인합니다", {
      duration: 2000,
      id: "answer-reveal-without-grading",
    });
    persistRevealedSessionState(userAnswer, true);
    setIsCorrectAnswerVisible(true);
  }, [persistRevealedSessionState, userAnswer]);

  const submitPartialAnswer = useCallback(async () => {
    // partialScore 가 null 이어도(빈칸이 1개뿐이라 totalItems===1 이거나, greedy 채점과
    // max-matching 판정이 어긋나 correctItems===totalItems 인 경우) 오답으로 채점·공개한다.
    // 예전처럼 early-return 하면 단일 빈칸 object/set 문제에서 오답 입력 시 채점 버튼이
    // 무반응(공개·이력 저장·토스트 모두 안 됨)이 되는 회귀가 발생한다.
    const isPartial = partialScore !== null;
    const attemptPartialScore = partialScore ?? undefined;

    setIsRevealedWithoutGrading(false);
    setIsPartialAnswerReveal(isPartial);
    toast(isPartial ? "부분 점수로 제출합니다." : "오답으로 제출합니다.", {
      duration: 2600,
      id: "answer-partial-reveal",
    });
    const scoreStatus = scoreStatusFromResult(false, partialScoreRatio);
    persistRevealedSessionState(
      userAnswer,
      false,
      isPartial,
      scoreStatus,
      partialScoreRatio
    );
    setIsCorrectAnswerVisible(true);

    examScore?.addAttempt(false, problemNumber, partialScoreRatio, id);

    if (id && !disableHistory) {
      const clientToken = crypto.randomUUID();

      if (isSignedIn && user) {
        const tempAttempt = {
          timestamp: nowTimestamp(),
          isCorrect: false,
          userAnswer,
          partialScore: attemptPartialScore,
        };
        setProblemHistory((prev) => ({
          userId: user.id,
          problemId: id,
          attempts: [...(prev?.attempts || []), tempAttempt],
          lastAttemptAt: Date.now(),
        }));

        try {
          const token = await getTokenWithRetry(getToken);
          if (!token) {
            toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
            setTimeout(() => {
              window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
            }, 1500);
            const result = await getProblemHistoryById(id);
            if (result.success) {
              setProblemHistory(result.history);
            }
            return;
          }
          const saveResult = await retryServerAction<Awaited<ReturnType<typeof saveProblemHistory>>>(() => {
            return saveProblemHistory(id, false, userAnswer, { partialScore: attemptPartialScore, clientToken });
          });
          if (!saveResult.success) {
            if (saveResult.error !== "인증이 필요합니다.") toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
            if (saveResult.error === "인증이 필요합니다.") {
              toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
              setTimeout(() => {
                window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
              }, 1500);
            }
            const result = await getProblemHistoryById(id);
            if (result.success) {
              setProblemHistory(result.history);
            }
            return;
          }
          if (saveResult.trialActivated) {
            setShowTrialActivatedModal(true);
            localStorage.setItem('trialToastShown', 'true');
          }
          const result = await getProblemHistoryById(id);
          if (result.success) {
            setProblemHistory(result.history);
          }
          checkMilestone(saveResult);
        } catch (error) {
          if (handleServerActionError(error)) {
            return;
          }
          console.error("풀이 결과 저장 실패:", error);
          Sentry.captureException(error, {
            tags: {
              component: 'ProblemAnswer',
              operation: 'saveProblemHistory'
            },
            extra: { problemId: id, isCorrect: false, userAnswer, partialScore }
          });
          const result = await getProblemHistoryById(id);
          if (result.success) {
            setProblemHistory(result.history);
          }
        }
      } else {
        const sessionId = getAnonymousSessionId();
        if (sessionId) {
          const tempAttempt = {
            timestamp: nowTimestamp(),
            isCorrect: false,
            userAnswer,
            partialScore: attemptPartialScore,
          };
          setProblemHistory((prev) => ({
            userId: sessionId,
            problemId: id,
            attempts: [...(prev?.attempts || []), tempAttempt],
            lastAttemptAt: Date.now(),
          }));

          try {
            const saveResult = await retryServerAction<Awaited<ReturnType<typeof saveProblemHistoryAnonymous>>>(() => {
              return saveProblemHistoryAnonymous(
                sessionId,
                id,
                false,
                userAnswer,
                { partialScore: attemptPartialScore, clientToken }
              );
            });
            if (!saveResult.success) toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
            const result = await getProblemHistoryBySessionId(
              sessionId,
              id
            );
            if (result.success) {
              setProblemHistory(result.history);
            }
            if (!saveResult.success) return;
            checkMilestone(saveResult);
          } catch (error) {
            if (handleServerActionError(error)) {
              return;
            }
            console.error("풀이 결과 저장 실패:", error);
            Sentry.captureException(error, {
              tags: {
                component: 'ProblemAnswer',
                operation: 'saveProblemHistoryAnonymous'
              },
              extra: { sessionId, problemId: id, isCorrect: false, userAnswer, partialScore }
            });
            const result = await getProblemHistoryBySessionId(
              sessionId,
              id
            );
            if (result.success) {
              setProblemHistory(result.history);
            }
          }
        }
      }

      const isTargetUser = !isSignedIn || passType === 'free';
      if (isTargetUser) {
        const newCount = incrementProblemSolvedCount();
        if (newCount === 5) {
          if (!hasGA5ProblemsConversionSent()) {
            sendGAEventSafe('event', 'engaged_user_milestone', {
              milestone: '5_problems_solved',
              user_type: isSignedIn ? 'free_member' : 'anonymous',
              debug_mode: process.env.NODE_ENV === 'development',
            });
            markGA5ProblemsConversionSent();
          }
          if (!hasMetaPixel5ProblemsSent()) {
            sendMetaPixelCustomEvent('EngagedUserMilestone', {
              milestone: '5_problems_solved',
              user_type: isSignedIn ? 'free_member' : 'anonymous',
            });
            markMetaPixel5ProblemsSent();
          }
        }
      }
    }
  }, [
    partialScore,
    persistRevealedSessionState,
    userAnswer,
    examScore,
    problemNumber,
    partialScoreRatio,
    id,
    disableHistory,
    isSignedIn,
    user,
    getToken,
    checkMilestone,
    passType,
  ]);

  const requestPartialAnswerSubmit = useCallback(() => {
    if (disableHistory || isConfirmDismissed(REVEAL_CONFIRM_DISMISSED_KEY)) {
      submitPartialAnswer();
      return;
    }

    setRevealConfirmMode("partial");
    setShowRevealConfirm(true);
  }, [disableHistory, submitPartialAnswer]);

  // 무채점 "바로 정답 보기" 진입점 (JCG-2510 가드).
  // - disableHistory(미리보기/관리자 모드): 팝업 없이 바로 공개
  // - "다시 보지 않기"로 dismiss 한 경우: 팝업 없이 바로 공개
  // - 그 외: 확인 다이얼로그만 열고 실제 공개는 보류
  const revealAnswerWithoutSaving = useCallback(() => {
    if (disableHistory) {
      performRevealWithoutGrading();
      return;
    }

    if (isConfirmDismissed(REVEAL_CONFIRM_DISMISSED_KEY)) {
      performRevealWithoutGrading();
      return;
    }

    setRevealConfirmMode("empty");
    setShowRevealConfirm(true);
  }, [disableHistory, performRevealWithoutGrading]);

  // 확인 다이얼로그에서 "정답 보기" 클릭 시: dismiss 저장(체크 시) 후 공개.
  const confirmRevealWithoutGrading = useCallback(() => {
    if (dontAskAgain) setConfirmDismissed(REVEAL_CONFIRM_DISMISSED_KEY);
    setShowRevealConfirm(false);
    setDontAskAgain(false);
    if (revealConfirmMode === "partial") {
      submitPartialAnswer();
      return;
    }
    performRevealWithoutGrading();
  }, [
    dontAskAgain,
    performRevealWithoutGrading,
    revealConfirmMode,
    submitPartialAnswer,
  ]);

  // 확인 다이얼로그 취소/바깥 클릭: 공개하지 않고 닫기만.
  const cancelRevealWithoutGrading = useCallback(() => {
    setShowRevealConfirm(false);
    setRevealConfirmMode("empty");
    setDontAskAgain(false);
  }, []);

  const handleRevealClick = async () => {
    // 정답 여부를 먼저 계산
    const isCorrect = checkAnswerCorrectness(
      userAnswer,
      validationCorrectAnswer,
      ignoreOrder,
      strictWhitespace,
      caseSensitive,
      compareAsSet,
      ignoreTrailingSemicolon,
      allowQuotedSqlAlias
    ) as boolean;

    if (isAnswerSets(correctAnswer) || isObjectAnswer(correctAnswer)) {
      if (isCorrect) {
        setIsRevealedWithoutGrading(false);
        setIsPartialAnswerReveal(false);
        persistRevealedSessionState(
          userAnswer,
          false,
          false,
          scoreStatusFromResult(isCorrect)
        );
        setIsCorrectAnswerVisible(true);

        // StickyTitle에 정답 피드백 전달
        const answerFeedbackEvent = new CustomEvent("answerFeedback", {
          detail: { isCorrect, isVisible: true },
        });
        window.dispatchEvent(answerFeedbackEvent);

        // 정답률 기록 (모의 시험용)
        examScore?.addAttempt(isCorrect, problemNumber, undefined, id);

        // 풀이 결과 저장
        if (id && !disableHistory) {
          const clientToken = crypto.randomUUID();

          if (isSignedIn && user) {
            // 로그인 사용자: optimistic update
            const tempAttempt = {
              timestamp: nowTimestamp(),
              isCorrect: isCorrect,
              userAnswer: userAnswer,
            };
            setProblemHistory((prev) => ({
              userId: user.id,
              problemId: id,
              attempts: [...(prev?.attempts || []), tempAttempt],
              lastAttemptAt: Date.now(),
            }));

            // 서버에 저장 후 실제 데이터로 교체
            try {
              // 세션 토큰 갱신 및 만료 확인 (JCG-699)
              const token = await getTokenWithRetry(getToken);
              if (!token) {
                toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
                setTimeout(() => {
                  window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
                }, 1500);
                const result = await getProblemHistoryById(id);
                if (result.success) {
                  setProblemHistory(result.history);
                }
                return;
              }
              const saveResult = await retryServerAction(() => {
                return saveProblemHistory(id, isCorrect, userAnswer, { clientToken });
              });
              // 저장 실패 시 optimistic update 롤백 (JCG-748: 익명 폴백 제거로 마이그레이션 루프 방지)
              if (!saveResult.success) {
                if (saveResult.error !== "인증이 필요합니다.") toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
                if (saveResult.error === "인증이 필요합니다.") {
                  toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
                  setTimeout(() => {
                    window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
                  }, 1500);
                }
                const result = await getProblemHistoryById(id);
                if (result.success) {
                  setProblemHistory(result.history);
                }
                return;
              }
              // 20문제 달성으로 무료체험 활성화된 경우 모달 표시
              if (saveResult.trialActivated) {
                setShowTrialActivatedModal(true);
                localStorage.setItem('trialToastShown', 'true');
              }
              const result = await getProblemHistoryById(id);
              if (result.success) {
                setProblemHistory(result.history);
              }
              // 마일스톤 체크 (저장 성공 후)
              checkMilestone(saveResult);
            } catch (error) {
              if (handleServerActionError(error)) {
                return;
              }
              console.error("풀이 결과 저장 실패:", error);
              Sentry.captureException(error, {
                tags: {
                  component: 'ProblemAnswer',
                  operation: 'saveProblemHistory'
                },
                extra: { problemId: id, isCorrect, userAnswer }
              });
              // 실패 시 임시 이력 제거 (롤백)
              const result = await getProblemHistoryById(id);
              if (result.success) {
                setProblemHistory(result.history);
              }
            }
          } else {
            // 비로그인 사용자: optimistic update
            const sessionId = getAnonymousSessionId();
            if (sessionId) {
              const tempAttempt = {
                timestamp: nowTimestamp(),
                isCorrect: isCorrect,
                userAnswer: userAnswer,
              };
              setProblemHistory((prev) => ({
                userId: sessionId,
                problemId: id,
                attempts: [...(prev?.attempts || []), tempAttempt],
                lastAttemptAt: Date.now(),
              }));

              // 서버에 저장 후 실제 데이터로 교체
              try {
                const saveResult = await retryServerAction(() => {
                  return saveProblemHistoryAnonymous(
                    sessionId,
                    id,
                    isCorrect,
                    userAnswer,
                    { clientToken }
                  );
                });
                if (!saveResult.success) toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
                const result = await getProblemHistoryBySessionId(
                  sessionId,
                  id
                );
                if (result.success) {
                  setProblemHistory(result.history);
                }
                if (!saveResult.success) return;
                // 마일스톤 체크 (저장 성공 후)
                checkMilestone(saveResult);
              } catch (error) {
                if (handleServerActionError(error)) {
                  return;
                }
                console.error("풀이 결과 저장 실패:", error);
                Sentry.captureException(error, {
                  tags: {
                    component: 'ProblemAnswer',
                    operation: 'saveProblemHistoryAnonymous'
                  },
                  extra: { sessionId, problemId: id, isCorrect, userAnswer }
                });
                // 실패 시 임시 이력 제거 (롤백)
                const result = await getProblemHistoryBySessionId(
                  sessionId,
                  id
                );
                if (result.success) {
                  setProblemHistory(result.history);
                }
              }
            }
          }

          // GA/Meta Pixel 전환 이벤트: 비로그인 또는 free plan 사용자가 5개 문제 풀이 시
          const isTargetUser = !isSignedIn || passType === 'free';
          if (isTargetUser) {
            const newCount = incrementProblemSolvedCount();
            if (newCount === 5) {
              // GA 이벤트
              if (!hasGA5ProblemsConversionSent()) {
                sendGAEventSafe('event', 'engaged_user_milestone', {
                  milestone: '5_problems_solved',
                  user_type: isSignedIn ? 'free_member' : 'anonymous',
                  debug_mode: process.env.NODE_ENV === 'development',
                });
                markGA5ProblemsConversionSent();
              }
              // Meta Pixel 이벤트
              if (!hasMetaPixel5ProblemsSent()) {
                sendMetaPixelCustomEvent('EngagedUserMilestone', {
                  milestone: '5_problems_solved',
                  user_type: isSignedIn ? 'free_member' : 'anonymous',
                });
                markMetaPixel5ProblemsSent();
              }
            }
          }
        }
      } else if (!answerScore.hasAnyAnswer) {
        revealAnswerWithoutSaving();
      } else if (answerScore.isComplete) {
        submitPartialAnswer();
      } else {
        requestPartialAnswerSubmit();
      }
    } else {
      if ((userAnswer as string).trim() !== "") {
        setIsRevealedWithoutGrading(false);
        setIsPartialAnswerReveal(false);
        persistRevealedSessionState(
          userAnswer,
          false,
          false,
          scoreStatusFromResult(isCorrect)
        );
        setIsCorrectAnswerVisible(true);

        // StickyTitle에 정답 피드백 전달
        const answerFeedbackEvent = new CustomEvent("answerFeedback", {
          detail: { isCorrect, isVisible: true },
        });
        window.dispatchEvent(answerFeedbackEvent);

        // 정답률 기록 (모의 시험용)
        examScore?.addAttempt(isCorrect, problemNumber, undefined, id);

        // 풀이 결과 저장 (optimistic update)
        if (id && !disableHistory) {
          const clientToken = crypto.randomUUID();

          if (isSignedIn && user) {
            // 로그인 사용자: optimistic update
            const tempAttempt = {
              timestamp: nowTimestamp(),
              isCorrect: isCorrect,
              userAnswer: userAnswer,
            };
            setProblemHistory((prev) => ({
              userId: user.id,
              problemId: id,
              attempts: [...(prev?.attempts || []), tempAttempt],
              lastAttemptAt: Date.now(),
            }));

            // 서버에 저장 후 실제 데이터로 교체
            try {
              // 세션 토큰 갱신 및 만료 확인 (JCG-699)
              const token = await getTokenWithRetry(getToken);
              if (!token) {
                toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
                setTimeout(() => {
                  window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
                }, 1500);
                const result = await getProblemHistoryById(id);
                if (result.success) {
                  setProblemHistory(result.history);
                }
                return;
              }
              const saveResult = await retryServerAction(() => {
                return saveProblemHistory(id, isCorrect, userAnswer, { clientToken });
              });
              // 저장 실패 시 optimistic update 롤백 (JCG-748: 익명 폴백 제거로 마이그레이션 루프 방지)
              if (!saveResult.success) {
                if (saveResult.error !== "인증이 필요합니다.") toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
                if (saveResult.error === "인증이 필요합니다.") {
                  toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
                  setTimeout(() => {
                    window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
                  }, 1500);
                }
                const result = await getProblemHistoryById(id);
                if (result.success) {
                  setProblemHistory(result.history);
                }
                return;
              }
              // 20문제 달성으로 무료체험 활성화된 경우 모달 표시
              if (saveResult.trialActivated) {
                setShowTrialActivatedModal(true);
                localStorage.setItem('trialToastShown', 'true');
              }
              const result = await getProblemHistoryById(id);
              if (result.success) {
                setProblemHistory(result.history);
              }
              // 마일스톤 체크 (저장 성공 후)
              checkMilestone(saveResult);
            } catch (error) {
              if (handleServerActionError(error)) {
                return;
              }
              console.error("풀이 결과 저장 실패:", error);
              Sentry.captureException(error, {
                tags: {
                  component: 'ProblemAnswer',
                  operation: 'saveProblemHistory'
                },
                extra: { problemId: id, isCorrect, userAnswer }
              });
              // 실패 시 임시 이력 제거 (롤백)
              const result = await getProblemHistoryById(id);
              if (result.success) {
                setProblemHistory(result.history);
              }
            }
          } else {
            // 비로그인 사용자: optimistic update
            const sessionId = getAnonymousSessionId();
            if (sessionId) {
              const tempAttempt = {
                timestamp: nowTimestamp(),
                isCorrect: isCorrect,
                userAnswer: userAnswer,
              };
              setProblemHistory((prev) => ({
                userId: sessionId,
                problemId: id,
                attempts: [...(prev?.attempts || []), tempAttempt],
                lastAttemptAt: Date.now(),
              }));

              // 서버에 저장 후 실제 데이터로 교체
              try {
                const saveResult = await retryServerAction(() => {
                  return saveProblemHistoryAnonymous(
                    sessionId,
                    id,
                    isCorrect,
                    userAnswer,
                    { clientToken }
                  );
                });
                if (!saveResult.success) toast.error(saveResult.error || "풀이 기록을 저장할 수 없습니다.", { id: "problem-save-error" });
                const result = await getProblemHistoryBySessionId(
                  sessionId,
                  id
                );
                if (result.success) {
                  setProblemHistory(result.history);
                }
                if (!saveResult.success) return;
                // 마일스톤 체크 (저장 성공 후)
                checkMilestone(saveResult);
              } catch (error) {
                if (handleServerActionError(error)) {
                  return;
                }
                console.error("풀이 결과 저장 실패:", error);
                Sentry.captureException(error, {
                  tags: {
                    component: 'ProblemAnswer',
                    operation: 'saveProblemHistoryAnonymous'
                  },
                  extra: { sessionId, problemId: id, isCorrect, userAnswer }
                });
                // 실패 시 임시 이력 제거 (롤백)
                const result = await getProblemHistoryBySessionId(
                  sessionId,
                  id
                );
                if (result.success) {
                  setProblemHistory(result.history);
                }
              }
            }
          }

          // GA/Meta Pixel 전환 이벤트: 비로그인 또는 free plan 사용자가 5개 문제 풀이 시
          const isTargetUser = !isSignedIn || passType === 'free';
          if (isTargetUser) {
            const newCount = incrementProblemSolvedCount();
            if (newCount === 5) {
              // GA 이벤트
              if (!hasGA5ProblemsConversionSent()) {
                sendGAEventSafe('event', 'engaged_user_milestone', {
                  milestone: '5_problems_solved',
                  user_type: isSignedIn ? 'free_member' : 'anonymous',
                  debug_mode: process.env.NODE_ENV === 'development',
                });
                markGA5ProblemsConversionSent();
              }
              // Meta Pixel 이벤트
              if (!hasMetaPixel5ProblemsSent()) {
                sendMetaPixelCustomEvent('EngagedUserMilestone', {
                  milestone: '5_problems_solved',
                  user_type: isSignedIn ? 'free_member' : 'anonymous',
                });
                markMetaPixel5ProblemsSent();
              }
            }
          }
        }
      } else {
        revealAnswerWithoutSaving();
      }
    }
  };

  // 정답 여부 계산 (렌더링용)
  const isCorrect = checkAnswerCorrectness(
    userAnswer,
    validationCorrectAnswer,
    ignoreOrder,
    strictWhitespace,
    caseSensitive,
    compareAsSet,
    ignoreTrailingSemicolon,
    allowQuotedSqlAlias
  ) as boolean;

  // 오답노트 저장 함수 (특정 노트 ID로 저장)
  const saveErrorNoteToFirestore = useCallback(
    async (noteId: string, content: string, images?: string[]) => {
      if (!hasErrorNoteAccess || !id || disableHistory || !noteId) return false;

      const draftAtSaveStart: ErrorNoteDraft = {
        content,
        images: [...(images ?? errorNotes.find(note => note.id === noteId)?.images ?? [])],
      };
      // 텍스트만 저장할 때는 서버의 최신 첨부를 보존한다.
      const imagesToSave = isSameErrorNoteImages(draftAtSaveStart.images, lastSavedNoteByIdRef.current.get(noteId)?.images)
        ? undefined
        : draftAtSaveStart.images;
      const pendingDrafts = pendingErrorNoteDraftsRef.current.get(noteId) ?? new Set<ErrorNoteDraft>();
      pendingDrafts.add(draftAtSaveStart);
      pendingErrorNoteDraftsRef.current.set(noteId, pendingDrafts);
      setErrorNoteSaveStates(prev => ({
        ...prev,
        [noteId]: {
          pending: [...(prev[noteId]?.pending ?? []), draftAtSaveStart],
          latestRequest: draftAtSaveStart,
          failed: false,
        },
      }));
      let didSave = false;
      const editVersionAtSaveStart = getErrorNoteEditVersion(noteId);
      const contentAtSaveStart = content;
      const imageFileMapAtSaveStart = new Map(inlineErrorNoteImageFileMapRef.current);
      try {
        const processedContent = await uploadBlockEditorImages({
          content: contentAtSaveStart,
          imageFileMap: imageFileMapAtSaveStart,
          uploadEndpoint: "/api/upload-error-note-image",
        });

        // 세션 토큰 갱신 및 만료 확인 (JCG-1315)
        const token = await getTokenWithRetry(getToken);
        if (!token) {
          toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
          setTimeout(() => {
            window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
          }, 1500);
          return false;
        }
        // 레거시 노트인 경우 기존 API 사용
        if (noteId === "legacy") {
          const result = await saveErrorNote(id, processedContent, imagesToSave);
          if (!result.success) throw new Error(result.error);
        } else {
          // JCG-2059: 노트가 속한 회차 문서로 라우팅 (canonical이면 undefined)
          const owner = errorNotes.find(n => n.id === noteId)?._ownerExamSessionId;
          const result = await saveErrorNoteItem(id, noteId, processedContent, imagesToSave, owner);
          if (!result.success) throw new Error(result.error);
        }
        didSave = true;
        const updatedAt = Date.now();
        const savedImages = imagesToSave ?? lastSavedNoteByIdRef.current.get(noteId)?.images ?? draftAtSaveStart.images;
        lastSavedNoteByIdRef.current.set(noteId, { content: processedContent, images: savedImages, updatedAt });
        const hasEditedBeforeSaveCompleted =
          getErrorNoteEditVersion(noteId) !== editVersionAtSaveStart;

        if (!hasEditedBeforeSaveCompleted) {
          if (activeNoteRef.current?.id === noteId) {
            inlineErrorNoteImageFileMapRef.current = new Map();
          }
          setErrorNotes(prev => prev.map(note =>
            note.id === noteId
              ? { ...note, content: processedContent, images: savedImages, updatedAt }
              : note
          ));
        }
        // 추가 입력이 있으면 초안과 입력 기준 8초 타이머를 그대로 유지한다.
        return { hasNewerChanges: hasEditedBeforeSaveCompleted };
      } catch (error) {
        if (handleServerActionError(error)) return false;
        // 세션 만료 시 로그인 페이지로 리다이렉트 (Fixes JCG-GAMJA-PRODUCTION-44)
        if (error instanceof Error && error.message === "인증이 필요합니다.") {
          toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
          setTimeout(() => {
            window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
          }, 1500);
          return false;
        }
        console.error("오답노트 저장 실패:", error);
        Sentry.captureException(error, {
          tags: { component: 'ProblemAnswer', operation: 'saveErrorNote' },
          extra: { problemId: id, noteId, noteLength: content.length },
        });
        toast.error("오답노트 저장에 실패했습니다.");
        return false;
      } finally {
        pendingDrafts.delete(draftAtSaveStart);
        if (pendingDrafts.size === 0) pendingErrorNoteDraftsRef.current.delete(noteId);
        setErrorNoteSaveStates(prev => {
          const state = prev[noteId];
          if (!state) return prev;
          return {
            ...prev,
            [noteId]: {
              ...state,
              pending: state.pending.filter(draft => draft !== draftAtSaveStart),
              failed: state.latestRequest === draftAtSaveStart ? !didSave : state.failed,
            },
          };
        });
      }
    },
    [
      id,
      disableHistory,
      hasErrorNoteAccess,
      getErrorNoteEditVersion,
      isSignedIn,
      user,
      getToken,
      errorNotes,
    ]
  );

  // 언마운트 시 미저장 flush를 위한 ref (effect에서만 사용되므로 useEffect로 동기화)
  const activeNoteRef = useRef(activeNote);
  const saveRef = useRef(saveErrorNoteToFirestore);
  useEffect(() => {
    activeNoteRef.current = activeNote;
  }, [activeNote]);
  useEffect(() => {
    saveRef.current = saveErrorNoteToFirestore;
  }, [saveErrorNoteToFirestore]);

  const activeNoteSavedSnapshot = activeNote ? lastSavedNoteByIdRef.current.get(activeNote.id) : undefined;

  // 오답노트 자동 저장 (마지막 입력 기준 debounce 8초)
  useEffect(() => {
    if (!activeNote || isCreatingNote) return;
    // 내용이 변경되지 않았으면 저장하지 않음
    if (!hasUnsavedErrorNote(activeNote)) return;

    // 기존 타이머 취소
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // 이전 요청이 끝나도 추가 입력의 저장 예정 시각을 늦추지 않는다.
    const lastEditedAt = errorNoteLastEditAtRef.current.get(activeNote.id) ?? Date.now();
    const delay = Math.max(0, lastEditedAt + ERROR_NOTE_AUTO_SAVE_DELAY_MS - Date.now());
    autoSaveTimerRef.current = setTimeout(async () => {
      autoSaveTimerRef.current = null;
      if (!hasUnsavedErrorNote(activeNote)) return;
      const pendingDrafts = pendingErrorNoteDraftsRef.current.get(activeNote.id);
      if (pendingDrafts && [...pendingDrafts].some(draft => isSameErrorNoteDraft(activeNote, draft))) return;
      const saved = await saveErrorNoteToFirestore(activeNote.id, activeNote.content, activeNote.images);
      if (saved && !saved.hasNewerChanges) {
        toast.success("오답노트가 자동 저장되었습니다.", { duration: 2000 });
      }
    }, delay);

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [activeNote, activeNoteSavedSnapshot, isCreatingNote, saveErrorNoteToFirestore, hasUnsavedErrorNote]);

  // 컴포넌트 언마운트 시 미저장 내용 flush
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      const note = activeNoteRef.current;
      if (note && hasUnsavedErrorNote(note)) {
        saveRef.current(note.id, note.content, note.images);
      }
    };
  }, [hasUnsavedErrorNote]);

  // 오답노트 수동 저장 핸들러
  const handleSaveErrorNote = async () => {
    if (!activeNote) return;
    const saved = await saveErrorNoteToFirestore(activeNote.id, activeNote.content, activeNote.images);
    if (saved && !saved.hasNewerChanges) {
      toast.success("오답노트가 저장되었습니다.");
    }
  };

  // 현재 노트 내용 변경 핸들러
  const handleNoteContentChange = useCallback((content: string) => {
    if (!activeNoteId || creatingNoteRef.current) return;
    bumpErrorNoteEditVersion(activeNoteId);
    setErrorNotes(prev => prev.map(n =>
      n.id === activeNoteId ? { ...n, content } : n
    ));
  }, [activeNoteId, bumpErrorNoteEditVersion]);

  // 이미지 URL 변경 핸들러
  const handleErrorNoteImagesChange = async (newImageUrls: string[]) => {
    if (!activeNote) return;
    bumpErrorNoteEditVersion(activeNote.id);
    setErrorNotes(prev => prev.map(n =>
      n.id === activeNote.id ? { ...n, images: newImageUrls } : n
    ));
    const saved = await saveErrorNoteToFirestore(activeNote.id, activeNote.content, newImageUrls);
    if (saved && !saved.hasNewerChanges) {
      toast.success("이미지가 저장되었습니다.", { duration: 2000 });
    }
  };

  // 새 오답노트 생성
  const handleCreateNote = async () => {
    if (!hasErrorNoteAccess || !id || disableHistory || creatingNoteRef.current || isSavingErrorNote || isUploadingImage) return;
    creatingNoteRef.current = true;
    setIsCreatingNote(true);
    try {
      // 새 탭으로 이동하기 전에 기존 노트의 초안과 첨부 이미지를 저장한다.
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      if (activeNote && hasUnsavedErrorNote(activeNote)) {
        const saved = await saveErrorNoteToFirestore(activeNote.id, activeNote.content, activeNote.images);
        if (!saved) return;
      }

      const result = await createErrorNoteItem(id);
      if (result.success && result.note) {
        setErrorNotes(prev => [...prev, result.note!]);
        setActiveNoteId(result.note.id);
        lastSavedNoteByIdRef.current.set(result.note.id, result.note);
        inlineErrorNoteImageFileMapRef.current = new Map();
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      if (handleServerActionError(error)) return;
      console.error("오답노트 생성 실패:", error);
      toast.error("오답노트를 생성할 수 없습니다.");
    } finally {
      creatingNoteRef.current = false;
      setIsCreatingNote(false);
    }
  };

  // 오답노트 삭제
  const handleDeleteNote = async (noteId: string) => {
    if (!hasErrorNoteAccess || !id || disableHistory || isDeletingNote) return;
    // 레거시 노트 삭제는 기존 API로 빈 문자열 저장
    setIsDeletingNote(true);
    try {
      if (noteId === "legacy") {
        const result = await saveErrorNote(id, "");
        if (!result.success) throw new Error(result.error);
      } else {
        // JCG-2059: 노트가 속한 회차 문서로 라우팅 (canonical이면 undefined)
        const owner = errorNotes.find(n => n.id === noteId)?._ownerExamSessionId;
        const result = await deleteErrorNoteItem(id, noteId, owner);
        if (!result.success) throw new Error(result.error);
      }
      setErrorNotes(prev => {
        const filtered = prev.filter(n => n.id !== noteId);
        lastSavedNoteByIdRef.current.delete(noteId);
        errorNoteEditVersionRef.current.delete(noteId);
        errorNoteLastEditAtRef.current.delete(noteId);
        // 삭제한 노트가 활성 노트였으면 다른 노트로 전환
        if (activeNoteId === noteId) {
          const nextActiveNote = filtered[0] ?? null;
          setActiveNoteId(nextActiveNote?.id ?? null);
          inlineErrorNoteImageFileMapRef.current = new Map();
        }
        return filtered;
      });
      toast.success("오답노트가 삭제되었습니다.");
    } catch (error) {
      if (handleServerActionError(error)) return;
      console.error("오답노트 삭제 실패:", error);
      toast.error(error instanceof Error ? error.message : "오답노트를 삭제할 수 없습니다.");
    } finally {
      setIsDeletingNote(false);
    }
  };

  // 탭 전환 시 미저장 내용 flush 후 전환
  const handleNoteTabChange = useCallback((noteId: string) => {
    if (creatingNoteRef.current) return;
    // 현재 노트에 미저장 내용이 있으면 즉시 저장
    if (activeNoteId) {
      const currentNote = errorNotes.find(n => n.id === activeNoteId);
      if (currentNote && hasUnsavedErrorNote(currentNote)) {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
        }
        saveErrorNoteToFirestore(activeNoteId, currentNote.content, currentNote.images);
      }
    }

    const note = errorNotes.find(n => n.id === noteId);
    if (note) {
      setActiveNoteId(noteId);
      inlineErrorNoteImageFileMapRef.current = new Map();
    }
  }, [
    errorNotes,
    activeNoteId,
    saveErrorNoteToFirestore,
    hasUnsavedErrorNote,
  ]);

  // 탭 이름 편집 시작 (더블클릭)
  const handleTabDoubleClick = useCallback((noteId: string) => {
    if (noteMetadataPendingRef.current) return;
    const note = errorNotes.find(n => n.id === noteId);
    if (note) {
      setEditingTabId(noteId);
      setEditingTabTitle(note.title || "");
    }
  }, [errorNotes]);

  // 탭 이름 저장
  const handleTabRenameSubmit = useCallback(async (noteId: string) => {
    if (!hasErrorNoteAccess || !id || noteMetadataPendingRef.current) return;
    noteMetadataPendingRef.current = true;
    const trimmed = editingTabTitle.trim();
    setEditingTabId(null);

    // 서버 저장 (JCG-2059: 노트가 속한 회차 문서로 라우팅)
    try {
      const owner = errorNotes.find(n => n.id === noteId)?._ownerExamSessionId;
      const result = await renameErrorNoteItem(id, noteId, trimmed, owner);
      if (!result.success) throw new Error(result.error);
      setErrorNotes(prev => prev.map(n =>
        n.id === noteId ? { ...n, title: trimmed || undefined } : n
      ));
    } catch (error) {
      console.error("탭 이름 변경 실패:", error);
      toast.error(error instanceof Error ? error.message : "노트 제목을 변경할 수 없습니다.");
    } finally {
      noteMetadataPendingRef.current = false;
    }
  }, [id, editingTabTitle, hasErrorNoteAccess, errorNotes, renameErrorNoteItem]);

  // 드래그 시작
  const handleDragStart = useCallback((noteId: string) => {
    if (noteMetadataPendingRef.current) return;
    setDraggedNoteId(noteId);
  }, []);

  // 드래그 오버
  const handleDragOver = useCallback((e: React.DragEvent, noteId: string) => {
    e.preventDefault();
    if (draggedNoteId && draggedNoteId !== noteId) {
      setDragOverNoteId(noteId);
    }
  }, [draggedNoteId]);

  // 드롭 → 순서 변경
  const handleDrop = useCallback(async (targetNoteId: string) => {
    if (!hasErrorNoteAccess || !id || !draggedNoteId || draggedNoteId === targetNoteId || noteMetadataPendingRef.current) {
      setDraggedNoteId(null);
      setDragOverNoteId(null);
      return;
    }

    const oldIndex = errorNotes.findIndex(n => n.id === draggedNoteId);
    const newIndex = errorNotes.findIndex(n => n.id === targetNoteId);
    if (oldIndex === -1 || newIndex === -1) return;

    noteMetadataPendingRef.current = true;
    const reordered = [...errorNotes];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);
    setDraggedNoteId(null);
    setDragOverNoteId(null);

    // 서버 저장
    try {
      const noteIds = reordered.map(n => n.id);
      const result = await reorderErrorNotes(id, noteIds);
      if (!result.success) throw new Error(result.error);
      // 요청 중 변경된 본문은 보존하고 성공한 순서만 반영한다. 새 노트는 끝에 둔다.
      const positions = new Map(noteIds.map((noteId, index) => [noteId, index]));
      setErrorNotes(prev => [...prev].sort((a, b) =>
        (positions.get(a.id) ?? positions.size) - (positions.get(b.id) ?? positions.size)
      ));
    } catch (error) {
      console.error("노트 순서 변경 실패:", error);
      toast.error(error instanceof Error ? error.message : "노트 순서를 변경할 수 없습니다.");
    } finally {
      noteMetadataPendingRef.current = false;
    }
  }, [id, draggedNoteId, errorNotes, hasErrorNoteAccess, reorderErrorNotes]);

  const handleDragEnd = useCallback(() => {
    setDraggedNoteId(null);
    setDragOverNoteId(null);
  }, []);

  // 문제 저장 핸들러
  const handleSaveProblem = async () => {
    if (!id || disableHistory) return;

    try {
      if (isSignedIn && user) {
        // 세션 토큰 갱신 및 만료 확인 (JCG-1315)
        const token = await getTokenWithRetry(getToken);
        if (!token) {
          toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
          setTimeout(() => {
            window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
          }, 1500);
          return;
        }
        const result = await saveProblemAsSaved(id);
        if (result.success) {
          toast.success("문제가 저장되었습니다.");
          // problemHistory 업데이트
          const historyResult = await getProblemHistoryById(id);
          if (historyResult.success) {
            setProblemHistory(historyResult.history);
          }
        } else {
          throw new Error(result.error);
        }
      } else {
        const sessionId = getAnonymousSessionId();
        if (sessionId) {
          const result = await saveProblemAsSavedAnonymous(sessionId, id);
          if (result.success) {
            toast.success("문제가 저장되었습니다.");
            // problemHistory 업데이트
            const historyResult = await getProblemHistoryBySessionId(
              sessionId,
              id
            );
            if (historyResult.success) {
              setProblemHistory(historyResult.history);
            }
          } else {
            throw new Error(result.error);
          }
        }
      }
    } catch (error) {
      // Server Action not found 에러인 경우 페이지 새로고침 안내
      if (handleServerActionError(error)) {
        return;
      }
      console.error("문제 저장 실패:", error);
      toast.error("문제 저장에 실패했습니다.");
    }
  };

  // 문제 저장 취소 핸들러
  const handleUnsaveProblem = async () => {
    if (!id || disableHistory) return;

    // 모바일에서 저장 취소 피드백 표시
    if (isSmallScreen) {
      setIsShowingUnsaveFeedback(true);
    }

    try {
      if (isSignedIn && user) {
        // 세션 토큰 갱신 및 만료 확인 (JCG-1315)
        const token = await getTokenWithRetry(getToken);
        if (!token) {
          toast.error("로그인 세션이 만료되었습니다. 재로그인 페이지로 이동합니다.", { duration: 3000, id: "auth-expired" });
          setTimeout(() => {
            window.location.href = `/sign-in?redirect_url=${encodeURIComponent(window.location.pathname)}`;
          }, 1500);
          return;
        }
        const result = await unsaveProblemAsSaved(id);
        if (result.success) {
          toast.success("저장이 취소되었습니다.");
          // problemHistory 업데이트
          const historyResult = await getProblemHistoryById(id);
          if (historyResult.success) {
            setProblemHistory(historyResult.history);
          }
          // 모바일에서 피드백 표시 후 원래 상태로 복귀
          if (isSmallScreen) {
            setTimeout(() => {
              setIsShowingUnsaveFeedback(false);
            }, 1000);
          }
        } else {
          throw new Error(result.error);
        }
      } else {
        const sessionId = getAnonymousSessionId();
        if (sessionId) {
          const result = await unsaveProblemAsSavedAnonymous(sessionId, id);
          if (result.success) {
            toast.success("저장이 취소되었습니다.");
            // problemHistory 업데이트
            const historyResult = await getProblemHistoryBySessionId(
              sessionId,
              id
            );
            if (historyResult.success) {
              setProblemHistory(historyResult.history);
            }
            // 모바일에서 피드백 표시 후 원래 상태로 복귀
            if (isSmallScreen) {
              setTimeout(() => {
                setIsShowingUnsaveFeedback(false);
              }, 1000);
            }
          } else {
            throw new Error(result.error);
          }
        }
      }
    } catch (error) {
      // Server Action not found 에러인 경우 페이지 새로고침 안내
      if (handleServerActionError(error)) {
        return;
      }
      console.error("문제 저장 취소 실패:", error);
      toast.error("저장 취소에 실패했습니다.");
      // 실패 시 피드백 상태 해제
      if (isSmallScreen) {
        setIsShowingUnsaveFeedback(false);
      }
    }
  };

  // 오답노트 패널 토글
  const toggleErrorNote = () => {
    if (!hasErrorNoteAccess) {
      setFeatureLockedModal("errorNote");
      return;
    }
    // 셸 풀이 모드에서는 오답노트 열림 상태를 셸이 소유(wrongOpen) — 이쪽으로 위임.
    if (useShellSolution) {
      setWrongOpen(!wrongOpen);
      return;
    }
    setIsErrorNoteOpen((prev) => !prev);
  };

  // ProblemAnswer 높이 계산 및 오답노트 높이 조정 (큰 화면에서만, 셸 풀이 모드는 제외)
  // 셸 모드에서는 50/50 분할 flex 레이아웃이 높이를 관리하므로 직접 height 조작이 충돌한다.
  useEffect(() => {
    if (problemAnswerRef && isErrorNoteOpen && !isSmallScreen && !useShellSolution) {
      const updateErrorNoteHeight = () => {
        if (!problemAnswerRef) return;

        const problemAnswerHeight = problemAnswerRef.offsetHeight;
        const navbarHeight = window.innerWidth >= 640 ? 64 : 48;
        const stickyTitle = document.getElementById("sticky-title");
        const stickyTitleHeight =
          stickyTitle && !stickyTitle.classList.contains("hidden")
            ? stickyTitle.offsetHeight
            : 0;
        const screenHeight = window.innerHeight;
        const screenMaxHeight =
          screenHeight - navbarHeight - stickyTitleHeight - 100;

        const errorNotePanel = document.getElementById("error-note-panel");
        const editorEl = errorNoteEditorRef.current;

        if (errorNotePanel && editorEl) {
          let targetHeight: number;

          if (problemAnswerHeight <= 400) {
            targetHeight = problemAnswerHeight;
          } else {
            const maxHeight = Math.min(problemAnswerHeight, screenMaxHeight);

            // flex-1 컨테이너의 scrollHeight 대신 자식 요소들의 실제 높이 합산
            let contentHeight = 0;
            for (const child of Array.from(editorEl.children)) {
              contentHeight += (child as HTMLElement).offsetHeight;
            }
            // 자식 간 gap (space-y-4 = 16px)
            const childCount = editorEl.children.length;
            if (childCount > 1) {
              contentHeight += (childCount - 1) * 16;
            }
            // 패딩 (p-4 = 16px * 2)
            contentHeight += 32;

            const headerHeight = 60;
            const buttonAreaHeight = 80;

            const minPanelHeight = headerHeight + buttonAreaHeight + 200;

            const panelHeightFromContent =
              headerHeight + buttonAreaHeight + contentHeight;

            targetHeight = Math.min(
              Math.max(minPanelHeight, panelHeightFromContent),
              maxHeight
            );
          }

          errorNotePanel.style.height = `${targetHeight}px`;
        }
      };

      // BlockEditor 렌더링 완료 후 높이 계산
      const timeoutId = setTimeout(updateErrorNoteHeight, 150);

      window.addEventListener("resize", updateErrorNoteHeight);

      return () => {
        clearTimeout(timeoutId);
        window.removeEventListener("resize", updateErrorNoteHeight);
      };
    }
  }, [
    problemAnswerRef,
    isErrorNoteOpen,
    isSolutionVisible,
    isSmallScreen,
    activeNote,
    useShellSolution,
  ]);

  // 패널 포커스 이벤트 처리 (필기 ↔ 풀이/오답노트 z-index 전환)
  const [isPanelFocused, setIsPanelFocused] = useState(false);
  const panelId = id ? `problem-${id}` : componentId;
  const wasAnswerVisibleRef = useRef(isCorrectAnswerVisible);
  useEffect(() => {
    const justRevealed = isCorrectAnswerVisible && !wasAnswerVisibleRef.current;
    wasAnswerVisibleRef.current = isCorrectAnswerVisible;
    if (!previewSolutionInShell || !useShellSolution || !justRevealed || !solution) return;
    // 관리자 미리보기에서는 채점/정답 공개 직후 해당 문제의 풀이를 우측에 연다.
    // 닫은 뒤 다시 열리지 않도록 공개 상태의 상승 변화에만 반응한다.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsSolutionVisible(true);
    enterSolution(panelId);
  }, [isCorrectAnswerVisible, previewSolutionInShell, useShellSolution, solution, enterSolution, panelId]);

  const handlePanelFocus = useCallback(() => {
    setIsPanelFocused(true);
    window.dispatchEvent(new CustomEvent("panel-focus", { detail: panelId }));
  }, [panelId]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail !== panelId) {
        setIsPanelFocused(false);
      }
    };
    window.addEventListener("panel-focus", handler);
    return () => window.removeEventListener("panel-focus", handler);
  }, [panelId]);

  // 풀이 사이드 패널 표시 여부 — 셸 풀이 모드(JCG-2065)에서는 인라인 사이드 패널을 모두 끄고
  // 우측 사이드바로 portal 한다. useShellSolution 이면 아래 인라인 레이아웃 플래그가 전부 false.
  const solutionPanelOpen =
    !useShellSolution && useSideLayout && isCorrectAnswerVisible && isSolutionVisible && !!solution;
  const inlineSolutionOpen =
    !useShellSolution && !useSideLayout && isCorrectAnswerVisible && isSolutionVisible && !!solution;
  // 사이드 패널 존재 여부 (overflow 및 width 제어용)
  const hasSidePanel =
    solutionPanelOpen || (!useShellSolution && hasErrorNoteAccess && isErrorNoteOpen && !isSmallScreen);
  // 3컬럼 레이아웃 (문제 | 풀이 | 오답노트)
  const isThreeColumnLayout = solutionPanelOpen && hasErrorNoteAccess && isErrorNoteOpen;

  // 오답노트 열림 소스: 셸 모드면 셸 소유(wrongOpen) 미러, 아니면 로컬(isErrorNoteOpen).
  // wrongOpen 은 셸 단일 소유 전역 상태라, 오답노트 목록처럼 다중 ProblemAnswer 가 같은 셸
  // 컨텍스트를 공유하는 화면에서 비활성 문제까지 errorNoteOpen=true 로 누수되는 것을 막기 위해
  // activeSolutionId === panelId 로 활성 문제에 한정한다(portal 가드 line 3461·handleErrorNoteButton
  // else 분기와 동일 기준).
  const errorNoteOpen = hasErrorNoteAccess && (useShellSolution
    ? activeSolutionId === panelId && wrongOpen
    : isErrorNoteOpen);

  // 셸 풀이 모드에서 다른 문제로 전환되거나(셸 X 또는 다른 문제 "풀이 보기") 풀이가 닫히면
  // 이 인스턴스의 isSolutionVisible 을 false 로 되돌려 "풀이 보기" 버튼을 다시 노출한다.
  useEffect(() => {
    if (useShellSolution && isSolutionVisible && activeSolutionId !== panelId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsSolutionVisible(false);
    }
  }, [useShellSolution, isSolutionVisible, activeSolutionId, panelId]);

  // 오답노트 복습(JCG-2060): 스크롤로 이 문제가 화면 중앙(활성)이 되면 우측에 풀이+오답노트를
  // 자동으로 연다(클릭 불필요). 풀이가 있으면 50/50, 없으면 풀이 영역이 비므로 오답노트 90%(0.1).
  // enterSolution 이 setWrongOpen(false) 로 두므로, 직후 setWrongOpen(true) 로 둘 다 열림을 보장한다.
  // 셸 외부 상태 동기화라 effect 가 적절(위 패턴과 동일). 비활성 문제는 동작 안 함 → 핸드오프는
  // 새 활성 문제의 enter 가 activeSolutionId 교체로 처리.
  //
  // 풀기 모드(오답노트 페이지 토글): defaultSolutionOpen=false 로 내려와 정답 확인 전에는
  // isCorrectAnswerVisible=false → 자동 열림을 막는다. 이 문제가 막 활성화된 시점에 직전 문제의
  // 패널이 남아 있으면 닫아 다른 문제의 풀이·오답노트가 옆에 잘못 노출되는 것을 방지한다.
  // (활성화 "시점"만 닫는 이유: 활성 중 다른 카드의 풀이보기 수동 클릭까지 닫아버리지 않도록.)
  // 복습 모드에서는 isCorrectAnswerVisible 이 항상 true 라 기존 동작 그대로다.
  const wasActiveReviewRef = useRef(false);
  const prevReviewWrongOpenRef = useRef(wrongOpen);
  useEffect(() => {
    // "셸 모드 활성 리뷰 패널" 여부를 한 번에 계산·동기화해 ref 갱신 경로를 일원화한다
    // (분기별 갱신 분산 시 셸 토글(useShellSolution 플랩) 후 becameActive 누락 위험).
    const activeShellReview = isActiveReviewPanel && useShellSolution;
    const becameActive = activeShellReview && !wasActiveReviewRef.current;
    // 풀기↔복습 토글로 defaultSolutionOpen 이 바뀐 커밋인지 감지한다. 그 커밋에선
    // activeSolutionId 가 (직전 exitSolution 여파로) null 이어도 사용자의 풀이 닫기가 아니므로
    // 아래 dismiss 분기를 건너뛴다.
    const defaultSolutionJustSynced =
      prevDefaultSolutionOpenForAutoOpenRef.current !== defaultSolutionOpen;
    prevDefaultSolutionOpenForAutoOpenRef.current = defaultSolutionOpen;
    const wrongCollapsed =
      activeShellReview &&
      activeSolutionId === panelId &&
      isSolutionVisible &&
      prevReviewWrongOpenRef.current &&
      !wrongOpen;
    const wrongExpanded =
      activeShellReview &&
      activeSolutionId === panelId &&
      isSolutionVisible &&
      !prevReviewWrongOpenRef.current &&
      wrongOpen;
    const solutionHidden =
      activeShellReview &&
      activeSolutionId === panelId &&
      isSolutionVisible &&
      solutionCollapsed;
    wasActiveReviewRef.current = activeShellReview;
    prevReviewWrongOpenRef.current = activeShellReview ? wrongOpen : false;
    if (!activeShellReview) {
      return;
    }
    if (!hasErrorNoteAccess) {
      if (activeSolutionId === panelId && wrongOpen) setWrongOpen(false);
      return;
    }
    if (!isCorrectAnswerVisible) {
      if (becameActive && activeSolutionId !== null) exitSolution();
      return;
    }
    if (solutionHidden) {
      reviewPanelAutoOpenDismissedRef.current = true;
      return;
    }
    if (wrongCollapsed) {
      reviewPanelAutoOpenDismissedRef.current = true;
      return;
    }
    if (wrongExpanded) {
      reviewPanelAutoOpenDismissedRef.current = false;
    }
    if (
      !becameActive &&
      !defaultSolutionJustSynced &&
      activeSolutionId === null &&
      isSolutionVisible
    ) {
      reviewPanelAutoOpenDismissedRef.current = true;
      setIsSolutionVisible(false);
      return;
    }
    if (reviewPanelAutoOpenDismissedRef.current) return;
    if (activeSolutionId !== panelId) {
      enterSolution(panelId);
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsSolutionVisible(true);
    }
    setWrongOpen(true);
    // 풀이 있으면 50/50, 풀이 없으면 placeholder 만 있어 오답노트를 90%(풀이 pane 0.1)로 키운다.
    setSplitRatio(solution ? 0.5 : 0.1);
  }, [
    isActiveReviewPanel,
    hasErrorNoteAccess,
    useShellSolution,
    isCorrectAnswerVisible,
    activeSolutionId,
    isSolutionVisible,
    wrongOpen,
    solutionCollapsed,
    defaultSolutionOpen,
    panelId,
    solution,
    enterSolution,
    exitSolution,
    setWrongOpen,
    setSplitRatio,
  ]);

  // 가운데 문제 카드 툴바(항상 노출)용 핸들러 — 셸 모드면 우측 사이드바를 변신/복원.
  const handleRevealSolution = useCallback(async () => {
    // JCG-2008: 새 풀이 누적 3개마다 1회 인터스티셜(ad-free=셸 모드에선 즉시 통과/no-op)
    await requestSolutionRevealAd(id);
    setIsSolutionVisible(true);
    if (useShellSolution) enterSolution(panelId);
  }, [requestSolutionRevealAd, id, useShellSolution, enterSolution, panelId]);

  const handleCloseSolution = useCallback(() => {
    const scrollElement = problemAnswerRef;
    const previousTop = scrollElement
      ? getProblemAnswerScrollAnchorPosition(scrollElement, "top")
      : undefined;
    if (useShellSolution && isActiveReviewPanel) {
      reviewPanelAutoOpenDismissedRef.current = true;
    }
    setIsSolutionVisible(false);
    if (useShellSolution) exitSolution();
    if (
      !useShellSolution &&
      scrollElement &&
      typeof previousTop === "number"
    ) {
      restoreProblemAnswerScrollPosition(scrollElement, "top", previousTop);
    }
  }, [
    useShellSolution,
    isActiveReviewPanel,
    exitSolution,
    problemAnswerRef,
  ]);

  const handleCloseInlineSolutionFromBottom = useCallback(() => {
    const scrollElement = problemAnswerRef;
    const previousBottom = scrollElement
      ? getProblemAnswerScrollAnchorPosition(scrollElement, "bottom")
      : undefined;
    const scrollBehavior = getInlineSolutionCollapseScrollBehavior();
    setIsSolutionVisible(false);
    if (scrollElement && typeof previousBottom === "number") {
      restoreProblemAnswerScrollPosition(
        scrollElement,
        "bottom",
        previousBottom,
        { behavior: scrollBehavior }
      );
    }
  }, [problemAnswerRef]);

  // 오답노트 버튼 — 셸 모드면 풀이 모드로 진입(미진입 시)하며 오답노트 분할을 펼친다.
  const handleErrorNoteButton = useCallback(() => {
    if (!hasErrorNoteAccess) {
      setFeatureLockedModal("errorNote");
      return;
    }
    if (useShellSolution) {
      if (activeSolutionId !== panelId) {
        reviewPanelAutoOpenDismissedRef.current = false;
        enterSolution(panelId);
        setIsSolutionVisible(true);
        setWrongOpen(true);
      } else {
        const nextWrongOpen = !wrongOpen;
        if (isActiveReviewPanel) {
          reviewPanelAutoOpenDismissedRef.current = !nextWrongOpen;
        }
        setWrongOpen(nextWrongOpen);
      }
      return;
    }
    setIsErrorNoteOpen((prev) => !prev);
  }, [
    hasErrorNoteAccess,
    useShellSolution,
    activeSolutionId,
    panelId,
    enterSolution,
    setWrongOpen,
    wrongOpen,
    isActiveReviewPanel,
  ]);

  // 사이드 패널의 sticky top 오프셋 (navbar + sticky 제목 높이)
  const [sidePanelTopOffset, setSidePanelTopOffset] = useState(0);
  useEffect(() => {
    if (!hasSidePanel) return;

    const updateOffset = () => {
      const navbarHeight = window.innerWidth >= 640 ? 64 : 48;

      // 이론 페이지의 sticky 제목
      const stickyTitle = document.getElementById("sticky-title");
      const stickyTitleHeight =
        stickyTitle && !stickyTitle.classList.contains("hidden")
          ? stickyTitle.offsetHeight
          : 0;

      // 오답노트 페이지의 sticky 필터
      const stickyFilter = document.getElementById("sticky-filter");
      const stickyFilterHeight = stickyFilter ? stickyFilter.offsetHeight : 0;

      setSidePanelTopOffset(navbarHeight + stickyTitleHeight + stickyFilterHeight + 16);
    };

    updateOffset();
    window.addEventListener("resize", updateOffset);

    // sticky 요소 표시/숨김 감지
    const observers: MutationObserver[] = [];

    const stickyTitle = document.getElementById("sticky-title");
    if (stickyTitle) {
      const obs = new MutationObserver(updateOffset);
      obs.observe(stickyTitle, { attributes: true, attributeFilter: ["class"] });
      observers.push(obs);
    }

    const stickyFilter = document.getElementById("sticky-filter");
    if (stickyFilter) {
      const obs = new MutationObserver(updateOffset);
      obs.observe(stickyFilter, { attributes: true, attributeFilter: ["class"] });
      observers.push(obs);
    }

    return () => {
      window.removeEventListener("resize", updateOffset);
      observers.forEach((obs) => obs.disconnect());
    };
  }, [hasSidePanel]);

  // 프리미엄 문제: 프리미엄 플랜 이상이 아니면 잠금 안내 표시
  if (premium && !isPremiumOrAbove) {
    const isFinalProblem = id?.includes("_final_") ?? false;
    const lockBadgeLabel = isFinalProblem ? "프리미엄 파이널 전용 문제" : "프리미엄 전용 문제";
    const lockBadgeIconBg = isFinalProblem
      ? "bg-violet-100 dark:bg-violet-900/40"
      : "bg-orange-100 dark:bg-orange-900/40";
    const lockBadgeIconColor = isFinalProblem ? "text-violet-500" : "text-yellow-500";
    const lockBadgeTitleColor = isFinalProblem
      ? "text-violet-700 dark:text-violet-300"
      : "text-orange-700 dark:text-orange-300";
    const lockBadgeDescColor = isFinalProblem
      ? "text-violet-600/70 dark:text-violet-400/70"
      : "text-orange-600/70 dark:text-orange-400/70";
    return (
      <div
        className="my-10 relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700"
        id={id ? `problem-${id}` : undefined}
        data-llm-copy-skip="true"
      >
        {/* 실제 문제처럼 보이는 더미 배경 (블러 처리) */}
        <div className="select-none pointer-events-none blur-[2px]" aria-hidden="true">
          {/* 헤더 */}
          <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <div className="w-16 h-5 bg-gray-200 dark:bg-gray-600 rounded" />
              <div className="w-10 h-5 bg-gray-200 dark:bg-gray-600 rounded" />
            </div>
            <div className="w-14 h-7 bg-gray-200 dark:bg-gray-600 rounded" />
          </div>
          {/* 문제 내용 */}
          <table className="w-full text-sm table-fixed">
            <tbody>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <td className="w-[50px] bg-blue-100 dark:bg-blue-900 p-3 font-semibold text-center align-top">문제</td>
                <td className="p-3">
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-full" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-4/5" />
                    <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/5" />
                  </div>
                </td>
              </tr>
              <tr>
                <td className="w-[50px] bg-green-100 dark:bg-green-900 p-3 font-semibold text-center align-top">답</td>
                <td className="p-3">
                  <div className="h-9 bg-gray-100 dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 w-full" />
                </td>
              </tr>
            </tbody>
          </table>
          {/* 하단 버튼 */}
          <div className="flex gap-2 p-3 border-t border-gray-200 dark:border-gray-700">
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-600 rounded" />
            <div className="h-8 w-20 bg-gray-200 dark:bg-gray-600 rounded" />
          </div>
        </div>

        {/* 오버레이 */}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/30 dark:bg-gray-900/30 backdrop-blur-none">
          <div className={`flex items-center justify-center w-12 h-12 rounded-full ${lockBadgeIconBg} mb-3`}>
            <Crown className={`w-6 h-6 ${lockBadgeIconColor}`} />
          </div>
          <p className={`text-sm font-medium ${lockBadgeTitleColor} mb-1`}>
            {lockBadgeLabel}
          </p>
          <p className={`text-xs ${lockBadgeDescColor} mb-4`}>
            이 문제는 프리미엄 이상 이용권에서 풀 수 있습니다.
          </p>
          <Button
            size="sm"
            className="bg-orange-500 hover:bg-orange-600 text-white"
            asChild
          >
            <Link href="/membership">
              <Crown className="w-3 h-3 mr-1" />
              이용권 보기
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const errorNoteCreatingStatus = isCreatingNote ? (
    <div role="status" className="shrink-0 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
      새 오답노트를 만드는 중입니다…
    </div>
  ) : null;

  const activeNoteSaveState = activeNote ? errorNoteSaveStates[activeNote.id] : undefined;
  const pendingNoteDraft = activeNoteSaveState?.pending.at(-1);
  const isSavingActiveNote = !!pendingNoteDraft;
  const isActiveNoteDirty = !!activeNote && hasUnsavedErrorNote(activeNote);
  const hasNewerNoteChanges = !!activeNote && !!pendingNoteDraft &&
    !isSameErrorNoteDraft(activeNote, pendingNoteDraft);
  const hasNoteSaveError = !isSavingActiveNote && isActiveNoteDirty && !!activeNoteSaveState?.failed;
  const noteSaveStatus = isSavingActiveNote
    ? hasNewerNoteChanges ? "저장 중 · 추가 변경사항은 미저장" : "저장 중…"
    : hasNoteSaveError
      ? "저장에 실패했습니다. 다시 저장해 주세요."
      : isActiveNoteDirty
        ? "변경사항이 저장되지 않았습니다"
        : activeNoteSavedSnapshot?.updatedAt ? `${formatRelativeTime(activeNoteSavedSnapshot.updatedAt)} 저장됨` : "저장됨";

  // 모든 오답노트 레이아웃에서 같은 상태와 저장 버튼을 사용한다.
  const errorNoteSaveControls = activeNote ? (
    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between gap-2">
        <span
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className={`min-w-0 text-xs ${hasNoteSaveError
            ? "text-red-600 dark:text-red-400"
            : isActiveNoteDirty || hasNewerNoteChanges
              ? "text-amber-700 dark:text-amber-400"
              : "text-gray-500 dark:text-gray-400"}`}
        >
          {noteSaveStatus}
        </span>
        <Button
          onClick={handleSaveErrorNote}
          disabled={isSavingActiveNote || isUploadingImage}
          className="shrink-0 bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
        >
          {isSavingActiveNote ? "저장 중…" : hasNoteSaveError ? "다시 저장" : "저장"}
        </Button>
      </div>
    </div>
  ) : null;

  // 오답노트 편집기 본문(탭 + BlockEditor + 이미지 + 저장) — 인라인 패널과 셸 풀이 모드 portal 이
  // 동일하게 재사용한다. createPortal 은 같은 컴포넌트 트리이므로 아래 핸들러 클로저가 그대로 동작.
  const errorNoteBody = hasErrorNoteAccess ? (
    <>
      {errorNoteCreatingStatus}
      <div className="contents" inert={isCreatingNote} aria-busy={isCreatingNote}>
      {/* 노트 탭 */}
      <div className="flex items-center gap-1 px-4 pt-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        {errorNotes.map((note, idx) => (
          <div
            key={note.id}
            draggable
            onDragStart={() => handleDragStart(note.id)}
            onDragOver={(e) => handleDragOver(e, note.id)}
            onDrop={() => handleDrop(note.id)}
            onDragEnd={handleDragEnd}
            className={`transition-all ${dragOverNoteId === note.id && draggedNoteId !== note.id ? "border-l-2 border-blue-400" : ""} ${draggedNoteId === note.id ? "opacity-40" : ""}`}
          >
            {editingTabId === note.id ? (
              <input
                autoFocus
                value={editingTabTitle}
                aria-label="오답노트 제목"
                onChange={(e) => setEditingTabTitle(e.target.value)}
                onBlur={() => handleTabRenameSubmit(note.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleTabRenameSubmit(note.id);
                  if (e.key === "Escape") setEditingTabId(null);
                }}
                className="w-20 px-2 py-1 text-sm border border-blue-400 rounded bg-white dark:bg-gray-800 dark:text-white outline-none"
              />
            ) : (
              <button
                onClick={() => handleNoteTabChange(note.id)}
                onDoubleClick={() => handleTabDoubleClick(note.id)}
                className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-t transition-colors cursor-grab active:cursor-grabbing ${
                  activeNoteId === note.id
                    ? "bg-white text-blue-700 border border-b-0 border-gray-200 dark:bg-gray-800 dark:text-blue-300 dark:border-gray-700 -mb-px"
                    : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                }`}
              >
                {note.title || `노트 ${idx + 1}`}
                {(() => {
                  const sLabel = effectiveResolveNoteSession?.(note.createdAt);
                  return sLabel ? (
                    <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-normal text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                      <History className="h-2.5 w-2.5" />
                      {sLabel}
                    </span>
                  ) : null;
                })()}
                {errorNotes.length > 1 && activeNoteId === note.id && (
                  <span
                    onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }}
                    className="ml-1 inline-flex min-h-6 min-w-6 items-center justify-center rounded hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/40"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </span>
                )}
              </button>
            )}
          </div>
        ))}
        <button
          type="button"
          onClick={handleCreateNote}
          disabled={isCreatingNote || isSavingErrorNote || isUploadingImage}
          className="disabled:cursor-not-allowed disabled:opacity-50 flex min-h-8 min-w-8 items-center justify-center rounded px-2 py-1.5 text-sm text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-gray-700 dark:hover:text-blue-400"
          aria-label="새 오답노트 만들기"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {activeNote ? (
        <>
          {/* 에디터 바깥 프레임(border)이 스크롤로 사라지지 않도록, 스크롤 주체를 BlockEditor
              내부 블록 영역으로 둔다 (JCG-2156). 셸은 fill(남은 높이를 채우고 블록 영역만 내부 스크롤
              → 핸들로 패널을 늘리면 에디터도 함께 늘어남), 인라인(JS 높이 자동계산)은 maxContentHeight
              로 블록 영역만 캡해 내부 스크롤시킨다. */}
          <div
            ref={errorNoteEditorRef}
            className={
              useShellSolution
                ? "flex-1 min-h-0 p-4 flex flex-col gap-4 overflow-hidden"
                : "flex-1 p-4 overflow-y-auto space-y-4"
            }
          >
            <BlockEditor
              key={activeNote.id}
              value={activeNote.content}
              onChange={handleNoteContentChange}
              onImageFilesChange={handleInlineErrorNoteImageFilesChange}
              placeholder="오답노트를 작성해주세요..."
              className={useShellSolution ? undefined : "min-h-[200px]"}
              fill={useShellSolution}
              maxContentHeight={useShellSolution ? undefined : "calc(100vh - 540px)"}
            />
            <div className={useShellSolution ? "shrink-0" : undefined}>
              <ErrorNoteImageUpload
                imageUrls={activeNote.images}
                onImageUrlsChange={handleErrorNoteImagesChange}
                maxImages={3}
                isUploading={isUploadingImage}
                onUploadingChange={setIsUploadingImage}
                disableUpload={true}
              />
            </div>
          </div>
          {errorNoteSaveControls}
        </>
      ) : (
        <div ref={errorNoteEditorRef} className="flex-1 p-8 flex items-center justify-center">
          <button
            type="button"
            onClick={handleCreateNote}
            disabled={isCreatingNote || isSavingErrorNote || isUploadingImage}
            className="disabled:cursor-not-allowed disabled:opacity-50 min-h-8 rounded-md px-3 py-1.5 text-sm text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-blue-400 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
          >
            + 새 오답노트 만들기
          </button>
        </div>
      )}
      </div>
    </>
  ) : null;

  // JCG-2320: 풀이 상단 "관련 이론" 링크 바. 정답·풀이 공개 후 풀이 본문 위에 sticky 로 고정되며
  // 사용자가 접을 수 있다(RelatedTheory 내부 상태). relatedTheory 데이터가 없으면 렌더하지 않는다.
  // 3개 렌더 지점(셸 portal / 인라인 / 사이드 패널)에서 공통으로 재사용한다.
  const relatedTheoryBar =
    relatedTheory && relatedTheory.length > 0 ? (
      <RelatedTheory links={relatedTheory} />
    ) : null;

  // 셸 풀이 모드(JCG-2065): 풀이 본문만 우측 사이드바에 portal. 문제·정답은 가운데 문제 카드에
  // 이미 노출되므로 중복으로 넣지 않는다. 복습 가리기(JCG-2060)면 우측 풀이도 모자이크로 덮는다.
  const solutionBody = (
    <div className="relative pb-2 overflow-x-auto whitespace-pre-wrap text-[14px] leading-relaxed dark:text-white">
      {solutionMosaic && (
        <MosaicOverlay
          label="풀이"
          onReveal={() => setMosaicReveal((r) => ({ ...r, solution: true }))}
        />
      )}
      {relatedTheoryBar}
      {/* 오답노트 복습(JCG-2060): 풀이가 없는 문제는 우측 풀이 영역에 안내만 보이고 셸이 오답노트를
          90% 로 키운다(setSplitRatio 0.1). 좁아진 풀이 pane 이 빈 줄처럼 보이지 않도록 점선 카드로
          감싸 "의도된 빈 상태"임을 드러낸다. 토큰(border-border/text-muted-foreground)만 써 다크 자동.
          복습 컨텍스트(errorNoteReviewCtx)에서만 적용 — 일반 페이지는 기존대로. */}
      {errorNoteReviewCtx && !solution ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-border px-4 py-5 text-center text-[13px] leading-relaxed text-muted-foreground">
          이 문제는 등록된 풀이가 없어요.
          <br />
          오답노트로 정리해 보세요.
        </div>
      ) : (
        <ProblemContentRenderer content={processedSolution} componentId={componentId} />
      )}
    </div>
  );

  return (
    <div
      className={`my-10 relative ${hasSidePanel ? (isPanelFocused ? "z-[8]" : "z-[5]") : "overflow-x-hidden"}`}
      id={id ? `problem-${id}` : undefined}
      data-exam-sessions={examSessions?.length ? JSON.stringify(examSessions) : undefined}
      data-tags={tags?.length ? JSON.stringify(tags) : undefined}
      data-llm-copy-skip="true"
    >
      <div
        className={`flex gap-4 transition-all duration-300 ease-in-out ${
          hasSidePanel ? "justify-center" : ""
        }`}
      >
        {/* 문제 영역 */}
        <div
          className={`${
            hasSidePanel
              ? isThreeColumnLayout
                ? "w-[400px] flex-shrink-0"
                : "w-[600px] flex-shrink-0"
              : "w-full"
          }`}
        >
          {/* 문제 번호 표시 (카드 바깥) */}
          {problemNumber != null && totalProblems != null && (
            <ProblemAnswerNumber
              problemNumber={problemNumber}
              totalProblems={totalProblems}
            />
          )}
        <ProblemAnswerCard
          ref={attachProblemAnswerRef}
        >
          {/* 풀이 이력 표시 헤더 */}
          {!disableHistory &&
            renderHistoryPanel?.({
              isSignedIn: isSignedIn ?? false,
              user,
              id,
              isLoadingHistory:
                isLoadingHistory || isLoadingHistoriesFromContext,
              problemHistory,
              isCorrectAnswerVisible,
              isCorrect: Boolean(isCorrect),
              hasPremiumAccess,
              problemTypes,
              premium,
              variationNote,
              showCurrentFeedback:
                !isRevealedWithoutGrading && !isPartialAnswerReveal,
            })}

          <ProblemAnswerTable>
            <tbody>
              {examSessions && examSessions.length > 0 && (
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="w-[50px] bg-red-100 p-3 font-semibold dark:bg-red-900 dark:text-white">
                    기출
                  </td>
                  <td className="p-3 bg-red-100 dark:bg-red-900 dark:text-white">
                    <div className="flex flex-wrap items-center gap-2">
                      {examSessions.map((session, index) => {
                        const examUrl = parseExamSessionUrl(session);
                        return examUrl ? (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="bg-red-400 text-white dark:bg-red-600 dark:text-white hover:bg-red-500 dark:hover:bg-red-700"
                            asChild
                          >
                            <Link href={examUrl} target="_blank">
                              {session}
                            </Link>
                          </Button>
                        ) : (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className="bg-red-400 text-white dark:bg-red-600 dark:text-white cursor-default"
                          >
                            {session}
                          </Button>
                        );
                      })}
                      {problemTypes?.includes("기출변형") && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-sm font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
                          기출변형
                          {variationNote && (
                            <Tooltip tapToOpen>
                              <TooltipTrigger asChild>
                                <button
                                  type="button"
                                  aria-label="기출변형 사유 보기"
                                  className="inline-flex min-h-7 min-w-7 items-center justify-center rounded-full text-purple-600 transition-colors hover:bg-purple-100 hover:text-purple-800 focus:outline-none focus:ring-2 focus:ring-purple-400 dark:text-purple-300 dark:hover:bg-purple-950/50 dark:hover:text-purple-100"
                                >
                                  <HelpCircle className="h-4 w-4" aria-hidden="true" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent
                                side="bottom"
                                className="text-sm max-w-80 md:!max-w-2xl !w-auto !text-left !text-wrap"
                              >
                                <div className="text-left whitespace-pre-line leading-relaxed">
                                  {variationNote}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              )}
              {notRecommended && (
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td
                    colSpan={2}
                    className="p-3 bg-amber-50 dark:bg-amber-900/20"
                  >
                    <div className="flex items-start gap-2 text-amber-700 dark:text-amber-300">
                      <ThumbsDown className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div className="text-sm">
                        <p className="font-medium">여러분의 열정을 이 문제에 낭비하지 마세요.</p>
                        {notRecommendedReason && (
                          <p className="font-normal mt-1">
                            {notRecommendedReason}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              )}
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                  문제
                </td>
                <td className="p-3 whitespace-pre-wrap dark:text-white text-base">
                  <div className="overflow-x-auto">
                    <ProblemContentRenderer
                      content={problem}
                      componentId={componentId}
                    />
                  </div>
                </td>
              </tr>

              {choices && choices.length > 0 && (
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                    보기
                  </td>
                  <td className="p-3 whitespace-pre-wrap dark:text-white">
                    <div className="relative overflow-hidden">
                      <div
                        className={`flex flex-wrap gap-2 transition-all duration-300 ${
                          !effectiveChoicesVisible ? "blur-sm pointer-events-none" : ""
                        }`}
                      >
                        {shuffledChoices.map((choice, index) => {
                          const isUsedInSeparator = isAnswerSeparatorWithChoices &&
                            separatorKeys.some(key => (userAnswer as Record<string, string | number>)[key]?.toString() === choice);
                          const shouldCopyChoice =
                            !isAnswerSeparatorWithChoices &&
                            !isChoiceAutoFill &&
                            !isChoiceTokenInsert &&
                            (choiceCopy || isTwoConsecutiveCorrect);
                          const isCopyFeedbackVisible = copiedIndex === index && shouldCopyChoice;
                          const isChoiceClickable = isAnswerSeparatorWithChoices || isChoiceAutoFill || isChoiceTokenInsert || shouldCopyChoice;
                          return (
                          <Button
                            key={index}
                            variant="outline"
                            size="sm"
                            className={`relative bg-gray-50 dark:bg-gray-700/50 dark:text-white w-auto h-auto min-w-0 max-w-full flex-shrink break-words whitespace-normal items-start text-left py-2 ${
                              isChoiceClickable
                                ? "cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600"
                                : "cursor-default pointer-events-none"
                            } ${
                              isCopyFeedbackVisible
                                ? "bg-green-100 dark:bg-green-900"
                                : isUsedInSeparator
                                  ? "bg-blue-50 text-blue-600 border-blue-300 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-700 opacity-60"
                                  : ""
                            }`}
                            data-choice-input={isChoiceAutoFill || isChoiceTokenInsert ? "" : undefined}
                            onPointerDown={(event) => {
                              if ((isChoiceAutoFill || isChoiceTokenInsert) && event.button === 0) event.preventDefault();
                            }}
                            onMouseDown={(event) => {
                              if ((isChoiceAutoFill || isChoiceTokenInsert) && event.button === 0) event.preventDefault();
                            }}
                            onClick={
                              isAnswerSeparatorWithChoices
                                ? () => handleSeparatorChoiceClick(choice)
                                : isChoiceAutoFill
                                  ? () => handleChoiceAutoFill(choice)
                                  : isChoiceTokenInsert
                                    ? () => handleChoiceTokenInsert(choice)
                                    : shouldCopyChoice
                                      ? async () => {
                                        try {
                                          await navigator.clipboard.writeText(
                                            choice
                                          );
                                          setCopiedIndex(index);
                                          setTimeout(() => {
                                            setCopiedIndex(null);
                                          }, 1500);
                                        } catch (err) {
                                          console.error("복사 실패:", err);
                                        }
                                      }
                                      : undefined
                            }
                            disabled={isCorrectAnswerVisible && (isAnswerSeparatorWithChoices || isChoiceAutoFill || isChoiceTokenInsert)}
                          >
                            {isCopyFeedbackVisible ? (
                              <>
                                {/* 텍스트 자리를 invisible로 유지해 복사 전후 버튼 폭·높이·줄바꿈 불변 */}
                                <span className="invisible" aria-hidden="true">
                                  {choice}
                                </span>
                                {/* 확보된 자리 위에 체크 아이콘을 중앙 정렬로 overlay */}
                                <span className="absolute inset-0 flex items-center justify-center">
                                  <Check className="h-4 w-4" aria-hidden="true" />
                                  <span className="sr-only">복사됨</span>
                                </span>
                              </>
                            ) : isUsedInSeparator ? (
                              <span className="flex items-center gap-1">
                                <Check className="h-3.5 w-3.5" />
                                {choice}
                              </span>
                            ) : (
                              choice
                            )}
                          </Button>
                          );
                        })}
                        {/* 보기 숨기기 버튼: 프리미엄 이상이고, 보기가 활성화되어 있고, 원래 비활성화되어야 했던 경우에만 표시 */}
                        {isPremiumOrAbove &&
                          !choiceCopy &&
                          !alwaysShowChoices &&
                          !globalChoicesHidden &&
                          isChoicesVisible &&
                          !isCorrectAnswerVisible &&
                          problemHistory &&
                          problemHistory.attempts.length > 0 &&
                          problemHistory.attempts[
                            problemHistory.attempts.length - 1
                          ].isCorrect && (
                            <Button
                              onClick={() => setIsChoicesVisible(false)}
                              variant="outline"
                              size="sm"
                              className="bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 dark:bg-orange-900 dark:text-orange-300 dark:border-orange-700 dark:hover:bg-orange-800 whitespace-nowrap"
                            >
                              {isSmallScreen ? (
                                <EyeOff className="h-4 w-4" />
                              ) : (
                                <>
                                  <EyeOff className="h-4 w-4 mr-1" />
                                  보기 숨기기
                                </>
                              )}
                            </Button>
                          )}
                      </div>
                      {/* 전체 보기 숨기기 오버레이 */}
                      {globalChoicesHidden && !effectiveChoicesVisible && (
                        <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-row items-center justify-center gap-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg px-4">
                          {isSmallScreen ? (
                            <>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                보기 숨김
                              </p>
                              <EyeOff className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                              <Button
                                onClick={() => setLocalChoicesOverride(true)}
                                variant="outline"
                                size="sm"
                                className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                전체 보기 숨김 모드
                              </p>
                              <Button
                                onClick={() => setLocalChoicesOverride(true)}
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800 whitespace-nowrap"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                이 문제 보기
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                      {/* 자동 보기 숨기기 오버레이 (프리미엄, 직전 정답) */}
                      {!globalChoicesHidden && isPremiumOrAbove && !isChoicesVisible && (
                        <div className="absolute top-0 left-0 right-0 bottom-0 flex flex-row items-center justify-center gap-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-lg px-4">
                          {isSmallScreen ? (
                            <>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                직전 정답 문제
                              </p>
                              <EyeOff className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                              <Button
                                onClick={() => setIsChoicesVisible(true)}
                                variant="outline"
                                size="sm"
                                className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <>
                              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                                직전 정답 문제네요!
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                보기 숨기기로 난이도 상승!
                              </p>
                              <Button
                                onClick={() => setIsChoicesVisible(true)}
                                variant="outline"
                                className="bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800 whitespace-nowrap"
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                보기 보이기
                              </Button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {shouldShowChoiceHint && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {choiceHintText}
                        {showTwoConsecutiveChoiceHint && (
                          <span className="ml-1 text-blue-600 dark:text-blue-400">
                            (직전 2회 연속 정답 혜택)
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              )}

              <tr className="border-b border-gray-200 dark:border-gray-700">
                <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                  답변
                </td>
                <td className="p-2">
                  {(isAnswerSets(correctAnswer) || isObjectAnswer(correctAnswer)) ? (
                    answerSeparator ? (
                      (() => {
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {separatorKeys.map((key, index, arr) => (
                                <div key={key} className="flex items-center gap-1.5">
                                  <input
                                    ref={(el) => {
                                      // ref callback은 render 중 실행되지 않으므로 .current mutate 안전
                                      // eslint-disable-next-line react-hooks/refs
                                      if (el) separatorInputRefs.current.set(index, el);
                                      else separatorInputRefs.current.delete(index);
                                    }}
                                    type="text"
                                    name={`${answerFieldNamePrefix}-${encodeURIComponent(key)}`}
                                    {...answerInputAutofillProps}
                                    inputMode={choices && choices.length > 0 ? "text" : separatorInputMode}
                                    value={
                                      (userAnswer as Record<string, string | number>)[
                                        key
                                      ] || ""
                                    }
                                    onChange={(e) => {
                                      const newValue = e.target.value;
                                      updateUserAnswer({
                                        ...(userAnswer as Record<string, string | number>),
                                        [key]: newValue,
                                      });
                                      if (
                                        shouldAutoAdvanceSeparatorInput &&
                                        Array.from(newValue).length >= 1 &&
                                        index < arr.length - 1
                                      ) {
                                        setTimeout(() => {
                                          separatorInputRefs.current.get(index + 1)?.focus();
                                        }, 0);
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      // 한글 IME 조합 중에는 Enter 키 처리 무시
                                      if (e.nativeEvent.isComposing) {
                                        return;
                                      }
                                      if (e.key === "Enter" && !e.shiftKey && hasKeyboard) {
                                        e.preventDefault();
                                        // eslint-disable-next-line react-hooks/refs -- DOM keydown 안에서 최신 쓰기 제한을 검사하며 렌더 중 실행하지 않는다.
                                        handleRevealClick();
                                      }
                                      if (
                                        e.key === "Backspace" &&
                                        !(userAnswer as Record<string, string | number>)[key] &&
                                        index > 0
                                      ) {
                                        e.preventDefault();
                                        separatorInputRefs.current.get(index - 1)?.focus();
                                      }
                                    }}
                                    // 이벤트 핸들러 내부에서만 ref 사용되므로 안전 (render 중 미실행)
                                    // eslint-disable-next-line react-hooks/refs
                                    onPaste={(e) => handleSeparatorPaste(e, separatorKeys)}
                                    placeholder={key}
                                    className={`rounded-md border border-gray-300 bg-gray-50 py-2 text-base text-black focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 ${choices && choices.length > 0 ? "w-full min-w-[80px] max-w-[180px] px-2 text-center" : "w-12 px-1 text-center"}`}
                                    disabled={isCorrectAnswerVisible}
                                    suppressHydrationWarning
                                  />
                                  {index < arr.length - 1 && (
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 flex-shrink-0">
                                      {answerSeparator}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                            {isTouchDevice && !isCorrectAnswerVisible && (
                              <button
                                type="button"
                                onClick={handleRevealClick}
                                className="w-full rounded-md bg-blue-500 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600 active:bg-blue-700"
                                aria-label="제출"
                              >
                                제출
                              </button>
                            )}
                          </div>
                        );
                      })()
                    ) : (
                    <div
                      className={
                        useGridLayout
                          ? `grid ${gridColsClass} gap-2`
                          : "space-y-3"
                      }
                    >
                      {getSortedKeys(
                        isAnswerSets(correctAnswer)
                          ? (correctAnswer[0] as Record<string, string | number | string[]>)
                          : (correctAnswer as Record<string, string | number | string[]>)
                      ).map((key) => (
                          <div
                            key={key}
                            className="flex items-center gap-2 min-w-0"
                          >
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit flex-shrink-0">
                              {key}:
                            </span>
                            <input
                              ref={(element) => {
                                if (element) choiceAnswerInputsRef.current.set(key, element);
                                else choiceAnswerInputsRef.current.delete(key);
                              }}
                              data-choice-answer={key}
                              type="text"
                              name={`${answerFieldNamePrefix}-${encodeURIComponent(key)}`}
                              {...answerInputAutofillProps}
                              value={
                                (userAnswer as Record<string, string | number>)[
                                  key
                                ] || ""
                              }
                              onChange={(e) => {
                                updateUserAnswer({
                                  ...(userAnswer as Record<string, string | number>),
                                  [key]: e.target.value,
                                });
                              }}
                              onKeyDown={(e) => {
                                // 한글 IME 조합 중에는 Enter 키 처리 무시
                                if (e.nativeEvent.isComposing) {
                                  return;
                                }
                                if (e.key === "Enter" && !e.shiftKey && hasKeyboard) {
                                  e.preventDefault();
                                  handleRevealClick();
                                }
                              }}
                              placeholder="답 입력"
                              className="flex-1 min-w-0 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base text-black focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
                              disabled={isCorrectAnswerVisible}
                              suppressHydrationWarning
                            />
                          </div>
                        ))}
                      {isTouchDevice && !isCorrectAnswerVisible && (
                        <button
                          type="button"
                          onClick={handleRevealClick}
                          className="mt-2 w-full rounded-md bg-blue-500 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600 active:bg-blue-700"
                          aria-label="제출"
                        >
                          제출
                        </button>
                      )}
                    </div>
                    )
                  ) : (
                    <div className="flex items-center gap-2">
                      <textarea
                        ref={answerTextareaRef}
                        data-choice-answer=""
                        name={`${answerFieldNamePrefix}-answer`}
                        {...answerInputAutofillProps}
                        value={userAnswer as string}
                        onChange={(e) => {
                          updateUserAnswer(e.target.value);
                          // 자동으로 높이 조절
                          e.target.style.height = "auto";
                          e.target.style.height = e.target.scrollHeight + "px";
                        }}
                        onKeyDown={(e) => {
                          // 한글 IME 조합 중에는 Enter 키 처리 무시 (맥 등)
                          if (e.nativeEvent.isComposing) {
                            return;
                          }
                          if (e.key === "Enter" && hasKeyboard) {
                            if (e.shiftKey) {
                              // 맥 한글 IME 환경에서 기본 Shift+Enter 줄바꿈이 동작하지 않는 문제 우회:
                              // 커서 위치에 직접 개행 문자 삽입
                              e.preventDefault();
                              const target = e.currentTarget;
                              const start = target.selectionStart;
                              const end = target.selectionEnd;
                              const currentValue = target.value;
                              const newValue =
                                currentValue.substring(0, start) +
                                "\n" +
                                currentValue.substring(end);
                              updateUserAnswer(newValue);
                              // 커서 위치 복원 및 높이 재조정 (DOM 업데이트 후)
                              requestAnimationFrame(() => {
                                target.selectionStart = target.selectionEnd =
                                  start + 1;
                                target.style.height = "auto";
                                target.style.height =
                                  target.scrollHeight + "px";
                              });
                            } else {
                              e.preventDefault();
                              handleRevealClick();
                            }
                          }
                        }}
                        placeholder={
                          hasKeyboard
                            ? "Enter = 제출, Shift+Enter = 줄바꿈"
                            : "답을 입력하세요"
                        }
                        className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-base text-black focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 resize-none overflow-hidden min-h-[38px]"
                        disabled={isCorrectAnswerVisible}
                        rows={1}
                        suppressHydrationWarning
                      />
                      {isTouchDevice && !isCorrectAnswerVisible && (
                        <button
                          type="button"
                          onClick={handleRevealClick}
                          className="shrink-0 rounded-md bg-blue-500 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-600 active:bg-blue-700"
                          aria-label="제출"
                        >
                          제출
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>

              <tr>
                <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                  정답
                </td>
                <td
                  className={`relative p-3 ${
                    !isCorrectAnswerVisible
                      ? "cursor-pointer text-center text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-600/50"
                      : ""
                  }`}
                  onClick={
                    !isCorrectAnswerVisible ? handleRevealClick : undefined
                  }
                >
                  {/* 복습 모드 가리기(JCG-2060): 정답이 보이는 상태에서만 모자이크로 덮는다. */}
                  {isCorrectAnswerVisible && answerMosaic && (
                    <MosaicOverlay
                      label="정답"
                      onReveal={() =>
                        setMosaicReveal((r) => ({ ...r, answer: true }))
                      }
                    />
                  )}
                  {isCorrectAnswerVisible ? (
                    <>
                      {shouldShowAnswerScore && (
                        <div
                          className={`mb-3 inline-flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border px-2.5 py-1.5 text-xs font-medium ${answerScoreClassName}`}
                        >
                          <span>
                            부분 점수 {answerScore.correctItems}/{answerScore.totalItems} ({answerScorePercent}%)
                          </span>
                          {isPartialAnswerReveal && (
                            <span className="text-[11px] opacity-80">
                              부분 제출
                            </span>
                          )}
                        </div>
                      )}
                      {isAnswerSets(validationCorrectAnswer) ? (
                      // 답 세트 배열: 가장 일치하는 세트 기준으로 개별 판정 + 화면용 유효 조합 표시
                      // (hiddenCorrectAnswers의 숨은 답 세트는 판정에만 쓰고 "다른 정답 조합"에는 나열하지 않는다)
                      <div className="space-y-3">
                        {(() => {
                          const answers = userAnswer as Record<string, string | number>;
                          const bestSet = findBestMatchingSet(
                            answers,
                            validationCorrectAnswer,
                            strictWhitespace,
                            caseSensitive,
                            ignoreTrailingSemicolon,
                            allowQuotedSqlAlias
                          );
                          const keys = getSortedKeys(bestSet as Record<string, string | number | string[]>);
                          return (
                            <div
                              className={
                                useGridLayout
                                  ? `grid ${gridColsClass} gap-2`
                                  : "space-y-2"
                              }
                            >
                              {keys.map((key) => {
                                const isItemCorrect = getItemCorrectness(
                                  key,
                                  bestSet[key],
                                  answers,
                                  bestSet as Record<string, string | number | string[]>,
                                  false,
                                  strictWhitespace,
                                  caseSensitive,
                                  undefined,
                                  ignoreTrailingSemicolon,
                                  allowQuotedSqlAlias
                                );
                                return (
                                  <div key={key} className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit">
                                      {key}:
                                    </span>
                                    <span
                                      className={
                                        isRevealedWithoutGrading || isItemCorrect
                                          ? "font-bold text-blue-600 dark:text-blue-400"
                                          : "font-bold text-red-600 dark:text-red-400"
                                      }
                                    >
                                      {bestSet[key]}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {isAnswerSets(correctAnswer) && correctAnswer.length > 1 && (
                          <div className="border-t border-gray-300 dark:border-gray-600 pt-2">
                            <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                              다른 정답 조합:
                            </div>
                            <div className="space-y-1">
                              {correctAnswer.map((set, idx) => {
                                const keys = getSortedKeys(set as Record<string, string | number | string[]>);
                                return (
                                  <div key={idx} className="text-sm text-green-600 dark:text-green-400">
                                    {keys.map((key) => `${key}: ${set[key]}`).join(", ")}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : isObjectAnswer(correctAnswer) ? (
                      ignoreOrder ? (
                        // ignoreOrder=true: 사용자 입력값 + 실제 정답 표시
                        <div className="space-y-4">
                          {/* 사용자 입력값 표시 */}
                          <div
                            className={
                              useGridLayout
                                ? `grid ${gridColsClass} gap-2`
                                : "space-y-2"
                            }
                          >
                            {getSortedKeys(correctAnswer).map((key) => {
                              const answers = userAnswer as Record<
                                string,
                                string | number
                              >;
                              const userInputValue = answers[key] || "";
                              const validationObj = (validationCorrectAnswer && typeof validationCorrectAnswer === "object" && !Array.isArray(validationCorrectAnswer) && !isAnswerSets(validationCorrectAnswer))
                                ? validationCorrectAnswer as Record<string, string | number | string[]>
                                : correctAnswer;
                              const isItemCorrect = getItemCorrectness(
                                key,
                                validationObj[key] || correctAnswer[key],
                                userAnswer as Record<string, string | number>,
                                validationObj,
                                ignoreOrder,
                                strictWhitespace,
                                caseSensitive,
                                correctAnswer as Record<string, string | number | string[]>,
                                ignoreTrailingSemicolon,
                                allowQuotedSqlAlias
                              );

                              return (
                                <div
                                  key={key}
                                  className="flex items-center gap-2"
                                >
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit">
                                    {key}:
                                  </span>
                                  <span
                                    className={
                                      isRevealedWithoutGrading
                                        ? "font-bold text-gray-600 dark:text-gray-300"
                                        : isItemCorrect
                                          ? "font-bold text-blue-600 dark:text-blue-400"
                                          : "font-bold text-red-600 dark:text-red-400"
                                    }
                                  >
                                    {userInputValue || "(입력 없음)"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>

                          {/* 구분선 */}
                          <div className="border-t border-gray-300 dark:border-gray-600 pt-3">
                            <div className="text-center text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
                              -- 정답 --
                            </div>

                            {/* 실제 정답 표시 */}
                            <div
                              className={
                                useGridLayout
                                  ? `grid ${gridColsClass} gap-2`
                                  : "space-y-2"
                              }
                            >
                              {getSortedEntries(correctAnswer).map(
                                ([key, value]) => (
                                  <div
                                    key={`correct-${key}`}
                                    className="flex items-center gap-2"
                                  >
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit">
                                      {key}:
                                    </span>
                                    <span className="font-bold text-green-600 dark:text-green-400">
                                      {Array.isArray(value) ? value.join(" 또는 ") : value}
                                    </span>
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        </div>
                      ) : answerSeparator ? (
                        // answerSeparator: flow 레이아웃으로 정답 표시
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {getSortedEntries(correctAnswer).map(
                            ([key, value], index, arr) => {
                              const validationObj = (validationCorrectAnswer && typeof validationCorrectAnswer === "object" && !Array.isArray(validationCorrectAnswer) && !isAnswerSets(validationCorrectAnswer))
                                ? validationCorrectAnswer as Record<string, string | number | string[]>
                                : correctAnswer;
                              const isItemCorrect = getItemCorrectness(
                                key,
                                validationObj[key] || value,
                                userAnswer as Record<string, string | number>,
                                validationObj,
                                ignoreOrder,
                                strictWhitespace,
                                caseSensitive,
                                undefined,
                                ignoreTrailingSemicolon,
                                allowQuotedSqlAlias
                              );
                              return (
                                <div key={key} className="flex items-center gap-1.5">
                                  <span
                                    className={`font-bold ${
                                      isRevealedWithoutGrading || isItemCorrect
                                        ? "text-blue-600 dark:text-blue-400"
                                        : "text-red-600 dark:text-red-400"
                                    }`}
                                  >
                                    {Array.isArray(value) ? value.join(" 또는 ") : value}
                                  </span>
                                  {index < arr.length - 1 && (
                                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                      {answerSeparator}
                                    </span>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      ) : (
                        // ignoreOrder=false: 기존 방식 (정답만 표시, 각 항목별 개별 판단)
                        <div
                          className={
                            useGridLayout
                              ? `grid ${gridColsClass} gap-2`
                              : "space-y-2"
                          }
                        >
                          {getSortedEntries(correctAnswer).map(
                            ([key, value]) => {
                              const validationObj = (validationCorrectAnswer && typeof validationCorrectAnswer === "object" && !Array.isArray(validationCorrectAnswer) && !isAnswerSets(validationCorrectAnswer))
                                ? validationCorrectAnswer as Record<string, string | number | string[]>
                                : correctAnswer;
                              const isItemCorrect = getItemCorrectness(
                                key,
                                validationObj[key] || value,
                                userAnswer as Record<string, string | number>,
                                validationObj,
                                ignoreOrder,
                                strictWhitespace,
                                caseSensitive,
                                undefined,
                                ignoreTrailingSemicolon,
                                allowQuotedSqlAlias
                              );
                              return (
                                <div
                                  key={key}
                                  className="flex items-center gap-2"
                                >
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-fit">
                                    {key}:
                                  </span>
                                  <span
                                    className={
                                      isRevealedWithoutGrading || isItemCorrect
                                        ? "font-bold text-blue-600 dark:text-blue-400"
                                        : "font-bold text-red-600 dark:text-red-400"
                                    }
                                  >
                                    {Array.isArray(value) ? value.join(" 또는 ") : value}
                                  </span>
                                </div>
                              );
                            }
                          )}
                        </div>
                      )
                    ) : Array.isArray(correctAnswer) ? (
                      // 배열 답안 처리 - 모든 정답 표시
                      <span
                        className={
                          isRevealedWithoutGrading || isCorrect
                            ? "font-bold text-blue-600 dark:text-blue-400 whitespace-pre-wrap"
                            : "font-bold text-red-600 dark:text-red-400 whitespace-pre-wrap"
                        }
                      >
                        {correctAnswer.join(" 또는 ")}
                      </span>
                    ) : (
                      // 단일 문자열 답안
                      <span
                        className={
                          isRevealedWithoutGrading || isCorrect
                            ? "font-bold text-blue-600 dark:text-blue-400 whitespace-pre-wrap"
                            : "font-bold text-red-600 dark:text-red-400 whitespace-pre-wrap"
                        }
                      >
                        {correctAnswer}
                      </span>
                    )}
                    </>
                  ) : (
                    "정답 보기"
                  )}
                </td>
              </tr>

              {/* JCG-3176: 정답 공개 시 이전에 입력한 답안 이력 (이번 제출분 제외) */}
              {isCorrectAnswerVisible && !disableHistory && previousAttempts.length > 0 && (
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white"></td>
                  <td className="p-3">
                    <PreviousAnswers
                      attempts={previousAttempts}
                      canView={hasPremiumAccess}
                      isSignedIn={!!isSignedIn}
                      onUpgrade={() => setFeatureLockedModal("problemHistory")}
                    />
                  </td>
                </tr>
              )}

              {/* 풀이보기 버튼 및 액션 버튼들 */}
              {isCorrectAnswerVisible && (
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white"></td>
                  <td className="p-3">
                    <div className="flex flex-wrap items-center gap-2">
                      {/* 토론 버튼 */}
                      {id && getForumUrl(id) && (
                        <Button
                          variant="outline"
                          className="bg-purple-50 text-purple-700 border-purple-300 hover:bg-purple-100 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700 dark:hover:bg-purple-800"
                          asChild
                        >
                          <Link href={`${getForumUrl(id)!}?from=${encodeURIComponent(pathname)}`} target="_blank">
                            <MessageSquare className="h-4 w-4" />
                            토론
                          </Link>
                        </Button>
                      )}

                      {/* 문제저장 버튼 */}
                      {!disableHistory && (
                      <div className="relative group">
                        <Button
                          onClick={
                            isPremiumOrAbove
                              ? () => {
                                  if (problemHistory?.saved) {
                                    handleUnsaveProblem();
                                  } else {
                                    handleSaveProblem();
                                  }
                                }
                              : () => setFeatureLockedModal("problemSave")
                          }
                          variant="outline"
                          onMouseEnter={() =>
                            isPremiumOrAbove &&
                            !isSmallScreen &&
                            problemHistory?.saved &&
                            setIsHoveringSaved(true)
                          }
                          onMouseLeave={() => setIsHoveringSaved(false)}
                          className={`transition-all duration-300 ease-in-out ${
                            !isPremiumOrAbove
                              ? "opacity-60 group-hover:opacity-80 "
                              : ""
                          }${
                            isSmallScreen && isShowingUnsaveFeedback
                              ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100 dark:bg-red-900 dark:text-red-300 dark:border-red-700 dark:hover:bg-red-800"
                              : problemHistory?.saved
                                ? isHoveringSaved
                                  ? "bg-red-50 text-red-700 border-red-300 hover:bg-red-100 dark:bg-red-900 dark:text-red-300 dark:border-red-700 dark:hover:bg-red-800"
                                  : "bg-yellow-50 text-yellow-700 border-yellow-300 hover:bg-yellow-100 dark:bg-yellow-900 dark:text-yellow-300 dark:border-yellow-700 dark:hover:bg-yellow-800"
                                : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                          }`}
                        >
                          {(() => {
                            if (isSmallScreen && isShowingUnsaveFeedback) {
                              return (
                                <>
                                  <X className="h-4 w-4" />
                                  저장취소
                                </>
                              );
                            }
                            if (problemHistory?.saved) {
                              return isHoveringSaved ? (
                                <>
                                  <X className="h-4 w-4" />
                                  저장취소
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  저장됨
                                </>
                              );
                            }
                            return (
                              <>
                                <Bookmark className="h-4 w-4" />
                                문제저장
                              </>
                            );
                          })()}
                        </Button>
                        {!isPremiumOrAbove && (
                          <button
                            type="button"
                            onClick={() =>
                              setFeatureLockedModal("problemSave")
                            }
                            className="absolute inset-0 flex items-center justify-center rounded-md bg-white/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 group-hover:bg-white/10 dark:bg-gray-800/30 dark:group-hover:bg-gray-800/10"
                            aria-label="문제 저장 기능 안내"
                          >
                            <div className="flex min-h-6 min-w-6 items-center justify-center gap-1 rounded bg-white/80 px-2 py-1 text-xs text-gray-600 transition-colors group-hover:bg-orange-100 group-hover:text-orange-600 dark:bg-gray-700/80 dark:text-gray-300 dark:group-hover:bg-orange-900/50 dark:group-hover:text-orange-400">
                              <Lock className="h-4 w-4 group-hover:hidden" aria-hidden="true" />
                              <LockOpen className="hidden h-4 w-4 group-hover:block" aria-hidden="true" />
                            </div>
                          </button>
                        )}
                      </div>
                      )}

                      {!disableHistory && id && (
                        <Button
                          type="button"
                          onClick={() =>
                            setIsRecommendationsOpen((open) => !open)
                          }
                          variant="outline"
                          aria-expanded={isRecommendationsOpen}
                          aria-controls={
                            isRecommendationsOpen
                              ? recommendationsPanelId
                              : undefined
                          }
                          className={
                            isRecommendationsOpen
                              ? "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-300 dark:border-amber-800 dark:hover:bg-amber-950"
                              : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                          }
                        >
                          {isRecommendationsOpen ? (
                            <>
                              <X className="h-4 w-4" aria-hidden="true" />
                              추천접기
                              <span className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-amber-400 dark:text-amber-950">
                                Beta
                              </span>
                            </>
                          ) : (
                            <>
                              <Sparkles
                                className="h-4 w-4"
                                aria-hidden="true"
                              />
                              추천문제
                              <span className="inline-flex items-center rounded-full bg-amber-500 px-1.5 py-0.5 text-[10px] font-semibold text-white dark:bg-amber-400 dark:text-amber-950">
                                Beta
                              </span>
                            </>
                          )}
                        </Button>
                      )}

                      {/* 구분 공백 */}
                      <div className="flex-1" />

                      {/* 풀이보기 버튼 — 셸 모드면 우측 사이드바를 풀이 모드로 변신/복원 */}
                      {solution &&
                        (!isSolutionVisible ? (
                          <Button
                            onClick={handleRevealSolution}
                            variant="outline"
                            className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-900 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-800"
                          >
                            <Lightbulb className="h-4 w-4" />
                            풀이보기
                          </Button>
                        ) : (
                          <Button
                            onClick={handleCloseSolution}
                            variant="outline"
                            className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-900 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-800"
                          >
                            <Lightbulb className="h-4 w-4" />
                            풀이접기
                          </Button>
                        ))}

                      {/* 오답노트 버튼 */}
                      {!disableHistory && (
                      <div className="relative group">
                        <Button
                          onClick={
                            isPremiumOrAbove
                              ? handleErrorNoteButton
                              : () => setFeatureLockedModal("errorNote")
                          }
                          variant="outline"
                          className={`${
                            !isPremiumOrAbove
                              ? "opacity-60 group-hover:opacity-80 transition-opacity "
                              : ""
                          }${
                            errorNoteOpen
                              ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800"
                              : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                          }`}
                        >
                          {errorNoteOpen ? (
                            <>
                              <X className="h-4 w-4" />
                              오답노트 닫기
                            </>
                          ) : (
                            <>
                              <BookOpen className="h-4 w-4" />
                              오답노트
                            </>
                          )}
                        </Button>
                        {!isPremiumOrAbove && (
                          <button
                            type="button"
                            onClick={() => setFeatureLockedModal("errorNote")}
                            className="absolute inset-0 flex items-center justify-center rounded-md bg-white/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 group-hover:bg-white/10 dark:bg-gray-800/30 dark:group-hover:bg-gray-800/10"
                            aria-label="오답노트 기능 안내"
                          >
                            <div className="flex min-h-6 min-w-6 items-center justify-center gap-1 rounded bg-white/80 px-2 py-1 text-xs text-gray-600 transition-colors group-hover:bg-orange-100 group-hover:text-orange-600 dark:bg-gray-700/80 dark:text-gray-300 dark:group-hover:bg-orange-900/50 dark:group-hover:text-orange-400">
                              <Lock className="h-4 w-4 group-hover:hidden" aria-hidden="true" />
                              <LockOpen className="hidden h-4 w-4 group-hover:block" aria-hidden="true" />
                            </div>
                          </button>
                        )}
                      </div>
                      )}

                      {/* 프리미엄 해제 버튼 (프리미엄 미만일 때만 표시) */}
                      {!disableHistory && !isPremiumOrAbove && (
                        <Button
                          variant="outline"
                          className="bg-orange-50 text-orange-600 border-orange-300 hover:bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400 dark:border-orange-700 dark:hover:bg-orange-900/50"
                          asChild
                        >
                          <Link
                            href="/membership"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="프리미엄 해제 이용권 페이지 새 탭에서 열기"
                          >
                            <Crown className="h-4 w-4" aria-hidden="true" />
                            <span className="hidden sm:inline">
                              프리미엄 해제
                            </span>
                          </Link>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {!disableHistory &&
                id &&
                isCorrectAnswerVisible &&
                isRecommendationsOpen && (
                  <tr
                    id={recommendationsPanelId}
                    className="border-b border-gray-200 dark:border-gray-700"
                  >
                    <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                      추천
                    </td>
                    <td className="p-3">
                      {id && renderRecommendations?.({ problemId: id })}
                    </td>
                  </tr>
                )}

              {/* 작은 화면일 때 오답노트를 문제 하단에 표시 (풀이보다 먼저) */}
              {isSmallScreen &&
                hasErrorNoteAccess &&
                isErrorNoteOpen &&
                (isCorrectAnswerVisible || defaultErrorNoteOpen) && (
                  <tr>
                    <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                      오답노트
                    </td>
                    <td className="p-3">
                      {errorNoteCreatingStatus}
                      <div inert={isCreatingNote} aria-busy={isCreatingNote} className="rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 flex flex-col">
                        {/* 노트 탭 */}
                        <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
                          {errorNotes.map((note, idx) => (
                            <div
                              key={note.id}
                              draggable
                              onDragStart={() => handleDragStart(note.id)}
                              onDragOver={(e) => handleDragOver(e, note.id)}
                              onDrop={() => handleDrop(note.id)}
                              onDragEnd={handleDragEnd}
                              className={`transition-all ${dragOverNoteId === note.id && draggedNoteId !== note.id ? "border-l-2 border-blue-400" : ""} ${draggedNoteId === note.id ? "opacity-40" : ""}`}
                            >
                              {editingTabId === note.id ? (
                                <input
                                  autoFocus
                                  value={editingTabTitle}
                                  aria-label="오답노트 제목"
                                  onChange={(e) => setEditingTabTitle(e.target.value)}
                                  onBlur={() => handleTabRenameSubmit(note.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") handleTabRenameSubmit(note.id);
                                    if (e.key === "Escape") setEditingTabId(null);
                                  }}
                                  className="w-16 px-1.5 py-0.5 text-xs border border-blue-400 rounded bg-white dark:bg-gray-800 dark:text-white outline-none"
                                />
                              ) : (
                                <button
                                  onClick={() => handleNoteTabChange(note.id)}
                                  onDoubleClick={() => handleTabDoubleClick(note.id)}
                                  className={`flex min-h-8 items-center gap-1 rounded px-2.5 py-1.5 text-xs whitespace-nowrap transition-colors cursor-grab active:cursor-grabbing ${
                                    activeNoteId === note.id
                                      ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
                                      : "text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700"
                                  }`}
                                >
                                  {note.title || `노트 ${idx + 1}`}
                                  {(() => {
                                    const sLabel = effectiveResolveNoteSession?.(note.createdAt);
                                    return sLabel ? (
                                      <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-gray-100 px-1 py-0.5 text-[9px] font-normal text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                        <History className="h-2 w-2" />
                                        {sLabel}
                                      </span>
                                    ) : null;
                                  })()}
                                  {errorNotes.length > 1 && activeNoteId === note.id && (
                                    <span
                                      onClick={(e) => { e.stopPropagation(); handleDeleteNote(note.id); }}
                                      className="ml-1 inline-flex min-h-6 min-w-6 items-center justify-center rounded hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-950/40"
                                    >
                                      <X className="h-4 w-4" aria-hidden="true" />
                                    </span>
                                  )}
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={handleCreateNote}
                            disabled={isCreatingNote || isSavingErrorNote || isUploadingImage}
                            className="disabled:cursor-not-allowed disabled:opacity-50 flex min-h-8 min-w-8 items-center justify-center rounded px-2 py-1.5 text-xs text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:hover:bg-gray-700 dark:hover:text-blue-400"
                            aria-label="새 오답노트 만들기"
                          >
                            <Plus className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                        {/* 복습 모드 가리기(JCG-2060): 작은 화면 인라인 노트도 탭(헤더)은 덮지 않고 본문만 감싼다. */}
                        <div className="relative">
                        {noteMosaic && (
                          <MosaicOverlay
                            label="오답노트"
                            onReveal={() =>
                              setMosaicReveal((r) => ({ ...r, note: true }))
                            }
                          />
                        )}
                        {activeNote ? (
                          <>
                            <div className="p-4 space-y-3">
                              <BlockEditor
                                key={activeNote.id}
                                value={activeNote.content}
                                onChange={handleNoteContentChange}
                                onImageFilesChange={handleInlineErrorNoteImageFilesChange}
                                placeholder="오답노트를 작성해주세요..."
                                className="min-h-[100px]"
                                maxContentHeight="200px"
                              />
                              <ErrorNoteImageUpload
                                imageUrls={activeNote.images}
                                onImageUrlsChange={handleErrorNoteImagesChange}
                                maxImages={3}
                                isUploading={isUploadingImage}
                                onUploadingChange={setIsUploadingImage}
                                disableUpload={true}
                              />
                            </div>
                            {errorNoteSaveControls}
                          </>
                        ) : (
                          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
                            <button
                              type="button"
                              onClick={handleCreateNote}
                              disabled={isCreatingNote || isSavingErrorNote || isUploadingImage}
                              className="disabled:cursor-not-allowed disabled:opacity-50 min-h-8 rounded-md px-3 py-1.5 text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-blue-400 dark:hover:bg-blue-950/40 dark:hover:text-blue-300"
                            >
                              + 새 오답노트 만들기
                            </button>
                          </div>
                        )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}

              {/* 풀이 내용 (하단 표시 - useSideLayout/셸 풀이 모드가 아닌 경우만) */}
              {inlineSolutionOpen && (
                <>
                  <tr>
                    <td className="w-[50px] bg-gray-50 p-3 font-semibold dark:bg-gray-700/50 dark:text-white">
                      내용
                    </td>
                    <td className="relative p-3 whitespace-pre-wrap dark:text-white text-base">
                      {solutionMosaic && (
                        <MosaicOverlay
                          label="풀이"
                          onReveal={() =>
                            setMosaicReveal((r) => ({ ...r, solution: true }))
                          }
                        />
                      )}
                      {relatedTheoryBar}
                      <div className="overflow-x-auto">
                        <ProblemContentRenderer
                          content={processedSolution}
                          componentId={componentId}
                        />
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="w-[50px] bg-gray-50 p-3 dark:bg-gray-700/50"></td>
                    <td className="p-3">
                      <Button
                        onClick={handleCloseInlineSolutionFromBottom}
                        variant="outline"
                        className="bg-green-50 text-green-700 border-green-300 hover:bg-green-100 dark:bg-green-900 dark:text-green-300 dark:border-green-700 dark:hover:bg-green-800"
                      >
                        <Lightbulb className="h-4 w-4" aria-hidden="true" />
                        풀이접기
                      </Button>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </ProblemAnswerTable>

          {/* 프리미엄 기능 잠금 모달 */}
          <FeatureLockedModal
            isOpen={featureLockedModal !== null}
            featureType={featureLockedModal || "errorNote"}
            onClose={() => setFeatureLockedModal(null)}
          />

          {/* 무료체험 활성화 모달 */}
          <TrialActivatedModal
            isOpen={showTrialActivatedModal}
            onClose={() => setShowTrialActivatedModal(false)}
          />

          {/* 마일스톤 팝업 */}
          {milestonePopup && (
            <MilestonePopup
              isOpen={milestonePopup.isOpen}
              milestone={milestonePopup.milestone}
              solvedCount={milestonePopup.solvedCount}
              totalProblems={milestonePopup.totalProblems}
              onClose={() => setMilestonePopup(null)}
            />
          )}
        </ProblemAnswerCard>
        </div>

        {/* 풀이 사이드 패널 (starter 이상 데스크탑) */}
        {solutionPanelOpen && (
          <div
            onMouseDown={handlePanelFocus}
            className={`${isThreeColumnLayout ? "w-[400px]" : "w-[600px]"} flex-shrink-0 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 flex flex-col transition-all duration-300 ease-in-out sticky self-start`}
            style={{ top: `${sidePanelTopOffset}px`, maxHeight: `calc(100vh - ${sidePanelTopOffset + 16}px)` }}
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold dark:text-white flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-green-600 dark:text-green-400" />
                풀이
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative group">
                  <Button
                    onClick={
                      isPremiumOrAbove
                        ? toggleErrorNote
                        : () => setFeatureLockedModal("errorNote")
                    }
                    variant="outline"
                    size="sm"
                    className={`${
                      !isPremiumOrAbove
                        ? "opacity-60 group-hover:opacity-80 transition-opacity "
                        : ""
                    }${
                      errorNoteOpen
                        ? "bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 dark:bg-blue-900 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800"
                        : "bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                    }`}
                  >
                    {errorNoteOpen ? (
                      <>
                        <X className="h-4 w-4" />
                        오답노트 닫기
                      </>
                    ) : (
                      <>
                        <BookOpen className="h-4 w-4" />
                        오답노트
                      </>
                    )}
                  </Button>
                  {!isPremiumOrAbove && (
                    <button
                      type="button"
                      onClick={() => setFeatureLockedModal("errorNote")}
                      className="absolute inset-0 flex items-center justify-center rounded-md bg-white/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 group-hover:bg-white/10 dark:bg-gray-800/30 dark:group-hover:bg-gray-800/10"
                      aria-label="오답노트 기능 안내"
                    >
                      <div className="flex min-h-6 min-w-6 items-center justify-center gap-1 rounded bg-white/80 px-2 py-1 text-xs text-gray-600 transition-colors group-hover:bg-orange-100 group-hover:text-orange-600 dark:bg-gray-700/80 dark:text-gray-300 dark:group-hover:bg-orange-900/50 dark:group-hover:text-orange-400">
                        <Lock className="h-4 w-4 group-hover:hidden" aria-hidden="true" />
                        <LockOpen className="hidden h-4 w-4 group-hover:block" aria-hidden="true" />
                      </div>
                    </button>
                  )}
                </div>
                <Button
                  onClick={handleCloseSolution}
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="relative flex-1 p-4 overflow-y-auto overscroll-contain">
              {solutionMosaic && (
                <MosaicOverlay
                  label="풀이"
                  onReveal={() =>
                    setMosaicReveal((r) => ({ ...r, solution: true }))
                  }
                />
              )}
              {relatedTheoryBar}
              <div className="whitespace-pre-wrap dark:text-white text-base overflow-x-auto">
                <ProblemContentRenderer
                  content={processedSolution}
                  componentId={componentId}
                />
              </div>
            </div>
          </div>
        )}

        {/* 오답노트 패널 (큰 화면에서만 사이드 패널로 표시 — 셸 풀이 모드는 portal 로 대체) */}
        {hasErrorNoteAccess && !useShellSolution && isErrorNoteOpen && !isSmallScreen && (
          <div
            id="error-note-panel"
            onMouseDown={handlePanelFocus}
            className={`${isThreeColumnLayout ? "w-[400px]" : "w-[600px]"} flex-shrink-0 rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800 flex flex-col transition-all duration-300 ease-in-out sticky self-start`}
            style={{ top: `${sidePanelTopOffset}px` }}
          >
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold dark:text-white">
                오답노트
              </h3>
              <Button
                onClick={toggleErrorNote}
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            {/* 복습 모드 가리기(JCG-2060): 헤더(닫기 버튼)는 덮지 않도록 본문만 감싼다. */}
            <div className="relative flex flex-1 flex-col overflow-hidden">
              {noteMosaic && (
                <MosaicOverlay
                  label="오답노트"
                  onReveal={() =>
                    setMosaicReveal((r) => ({ ...r, note: true }))
                  }
                />
              )}
              {errorNoteBody}
            </div>
          </div>
        )}
      </div>

      {/* 셸 풀이 모드(JCG-2065): 활성 문제의 풀이 본문/오답노트 편집기를 우측 사이드바 슬롯으로 portal.
          activeSolutionId === panelId 가드로 한 번에 하나의 문제만 그린다. */}
      {useShellSolution &&
        activeSolutionId === panelId &&
        solutionSlot &&
        createPortal(solutionBody, solutionSlot)}
      {useShellSolution &&
        activeSolutionId === panelId &&
        wrongOpen &&
        hasErrorNoteAccess &&
        errorNoteSlot &&
        createPortal(
          // 복습 가리기(JCG-2060): 우측 셸 오답노트 패널도 모자이크로 덮는다(클릭 시 드러남).
          <div className="relative flex h-full flex-col">
            {noteMosaic && (
              <MosaicOverlay
                label="오답노트"
                onReveal={() => setMosaicReveal((r) => ({ ...r, note: true }))}
              />
            )}
            {errorNoteBody}
          </div>,
          errorNoteSlot,
        )}

      {renderSolutionAd?.({ open: isSolutionAdOpen, onClose: closeSolutionAd })}

      {/* 무채점 "바로 정답 보기" 확인 다이얼로그 (JCG-2510):
          정답보기 버튼을 실수로 눌러 답을 보게 되는 불편 해소. 채점 제출 경로는 거치지 않는다. */}
      <Dialog
        open={showRevealConfirm}
        onOpenChange={(open) => {
          if (!open) cancelRevealWithoutGrading();
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {revealConfirmMode === "partial"
                ? "부분 점수로 제출하시겠습니까?"
                : "정말로 바로 정답을 확인하시겠습니까?"}
            </DialogTitle>
            <DialogDescription>
              {revealConfirmMode === "partial"
                ? "입력한 답 기준으로 칸수별 부분 점수를 계산하고 풀이 이력에 기록합니다."
                : "채점 없이 정답이 바로 공개됩니다."}
            </DialogDescription>
          </DialogHeader>
          {!externalSettings && (
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${componentId}-reveal-dont-ask`}
              checked={dontAskAgain}
              onCheckedChange={(checked) => setDontAskAgain(checked === true)}
            />
            <Label
              htmlFor={`${componentId}-reveal-dont-ask`}
              className="text-muted-foreground cursor-pointer font-normal"
            >
              다시 보지 않기
            </Label>
          </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={cancelRevealWithoutGrading}>
              취소
            </Button>
            <Button onClick={confirmRevealWithoutGrading}>
              {revealConfirmMode === "partial" ? "부분 제출" : "정답 보기"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProblemAnswer: React.FC<ProblemAnswerProps> = (props) => {
  return (
    <Suspense fallback={null}>
      <ProblemAnswerContent {...props} />
    </Suspense>
  );
};

export default ProblemAnswer;
